import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  ControlButton,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  reconnectEdge,
  useReactFlow,
  useStoreApi,
  SelectionMode,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import 'flag-icons/css/flag-icons.min.css';
import '../workflow/workflowEditor.css';
import {
  Box, Typography, Button, IconButton, Menu, MenuItem, Alert, Avatar,
  TextField, Slider, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, FormControl, InputLabel, Snackbar, Chip
} from '@mui/material';
import { ArrowLeft, Bot, Braces, Copy, ClipboardPaste, Map as MapIcon, Play, Plus, Ruler, Save, Search as SearchIcon, Sparkles, Trash2, X, Smartphone, RotateCcw, FolderOpen, Square, Smile, AlertTriangle } from 'lucide-react';
import NexusChatDrawer from '../components/NexusChatDrawer';
import GeminiReviewPanel from '../storyflow/GeminiReviewPanel';
import { useAuthContext } from '../context/AuthContext';
import { useTeam, TeamProvider } from '../context/TeamContext';
import { API_BASE_URL } from '../utils/api';
import BleuEdge, { BleuConnectionLine } from '../workflow/BleuEdge';
import { sfNodeTypes, SF_PALETTE_ITEMS, buildCharacterPaletteItems, SfCharactersProvider, SfLanguageProvider, SfActionsProvider } from '../storyflow/SfNodes';
import { parseLoremaClipboardText, importLoremaClipboard } from '../storyflow/LoreemaClipboard';
import SfEmojiPicker from '../storyflow/SfEmojiPicker';
import StoryflowLoader from '../storyflow/StoryflowLoader';
import {
  mergeGraphWithMeta, splitGraphMeta, newNodeId, newEdgeId
} from '../workflow/workflowGraphModel';
import StorageBrowserModal from '../components/StorageBrowserModal';
import AdminModal from '../components/AdminModal';
import VideoHoverPreview from '../components/VideoHoverPreview';
import { SCREEN_PRESETS } from '../game/gameTemplates';

const MUTED = 'rgba(161, 161, 170, 0.95)';

const edgeTypes = { bleu: BleuEdge };

const CTX_MENU_PAPER_SX = {
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  backgroundImage: 'none',
  boxShadow: '0 10px 40px rgba(0,0,0,0.45)'
};

/** Wrapper for the emoji picker used in Status node inspector.
 *  Uses a global flag pattern to let the parent button toggle the popover. */
function StatusEmojiPickerWrapper({ onSelect }) {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    window.__sfStatusEmojiForceUpdate = () => forceUpdate((n) => n + 1);
    return () => { window.__sfStatusEmojiForceUpdate = null; };
  }, []);

  const open = !!window.__sfStatusEmojiOpen;
  const anchor = window.__sfStatusEmojiAnchor || null;

  const handleClose = useCallback(() => {
    window.__sfStatusEmojiOpen = false;
    forceUpdate((n) => n + 1);
  }, []);

  const handleSelect = useCallback((emoji) => {
    onSelect(emoji);
    handleClose();
  }, [onSelect, handleClose]);

  return (
    <SfEmojiPicker
      anchorEl={anchor}
      open={open}
      onClose={handleClose}
      onSelect={handleSelect}
    />
  );
}

// ── Orphan edge cleanup helper ─────────────────────────────────
// Drops edges whose source/target node is missing or whose
// sourceHandle/targetHandle doesn't exist on the resolved node, per the
// handles each node type actually renders in SfNodes.js. For unknown node
// types we keep the edge (conservative — don't eat edges we don't understand).
function handlesForSfNode(node) {
  if (!node) return null;
  const data = node.data || {};
  switch (node.type) {
    case 'sfStart':
      return { src: new Set(['flow-out']), tgt: new Set() };
    case 'sfDialogue':
    case 'sfVideo': {
      const vCount = Math.max(
        (data.variants || []).length,
        (data.variants_en || []).length
      );
      const src = vCount > 0
        ? new Set(Array.from({ length: vCount }, (_, i) => `choice-${i}`))
        : new Set(['flow-out']);
      return { src, tgt: new Set(['flow-in']) };
    }
    case 'sfImage':
      return { src: new Set(['flow-out']), tgt: new Set(['flow-in']) };
    case 'sfNextChapter':
    case 'sfNextFlow':
      return { src: new Set(), tgt: new Set(['flow-in']) };
    case 'sfDecision': {
      const options = data.options
        || (data.optionA ? [data.optionA, data.optionB || 'Option B'] : ['Option A', 'Option B']);
      const src = new Set();
      const tgt = new Set();
      for (let i = 0; i < options.length; i++) {
        src.add(`decision-out-${i}`);
        tgt.add(`decision-in-${i}`);
      }
      return { src, tgt };
    }
    case 'sfBranch': {
      const opts = data.switchOptions
        || (data.branchOptionA ? [data.branchOptionA, data.branchOptionB || 'B'] : []);
      const src = new Set();
      for (let i = 0; i < opts.length; i++) src.add(`switch-out-${i}`);
      return { src, tgt: new Set(['flow-in']) };
    }
    case 'sfRpSwitch':
      return { src: new Set(['rp-out-pass', 'rp-out-fail']), tgt: new Set(['flow-in']) };
    case 'sfTimeskip':
    case 'sfIntroduce':
    case 'sfStatus':
    case 'sfInsta':
      return { src: new Set(['flow-out']), tgt: new Set(['flow-in']) };
    default:
      return null;
  }
}

function filterOrphanEdges(edgesList, nodesList) {
  const nodeById = new Map((nodesList || []).map((n) => [n.id, n]));
  let removed = 0;
  const unknownTypes = new Set();
  const kept = (edgesList || []).filter((e) => {
    const src = nodeById.get(e.source);
    const tgt = nodeById.get(e.target);
    if (!src || !tgt) { removed++; return false; }
    const srcHandles = handlesForSfNode(src);
    const tgtHandles = handlesForSfNode(tgt);
    if (srcHandles === null && src.type) unknownTypes.add(src.type);
    if (tgtHandles === null && tgt.type) unknownTypes.add(tgt.type);
    const sh = e.sourceHandle || 'flow-out';
    const th = e.targetHandle || 'flow-in';
    if (srcHandles && !srcHandles.src.has(sh)) { removed++; return false; }
    if (tgtHandles && !tgtHandles.tgt.has(th)) { removed++; return false; }
    return true;
  });
  if (unknownTypes.size > 0) {
    console.error(
      `[Storyflow] handlesForSfNode is missing entries for node type(s): ${[...unknownTypes].join(', ')}. Add them to handlesForSfNode() in StoryflowEditorPage.js so orphan edge cleanup validates their handles.`
    );
  }
  return { edges: kept, removed };
}

const CYRILLIC_RE = /[\u0400-\u04FF]/;

function StoryflowEditorInner() {
  const navigate = useNavigate();
  const { storyflowId } = useParams();
  const { getToken } = useAuthContext();
  const { activeTeam } = useTeam();
  const storeApi = useStoreApi();
  const { updateNodeInternals } = useReactFlow();
  const screenToFlowPosition = useCallback((pos) => {
    const { transform, domNode } = storeApi.getState();
    if (!domNode) return pos;
    const { x: domX, y: domY } = domNode.getBoundingClientRect();
    return {
      x: (pos.x - domX - transform[0]) / transform[2],
      y: (pos.y - domY - transform[1]) / transform[2],
    };
  }, [storeApi]);
  const getViewport = useCallback(() => {
    const [x, y, zoom] = storeApi.getState().transform;
    return { x, y, zoom };
  }, [storeApi]);
  const setCenter = useCallback((x, y, opts) => {
    storeApi.getState().setCenter(x, y, opts);
  }, [storeApi]);

  const [flowData, setFlowData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [teamStoryflows, setTeamStoryflows] = useState([]);

  const [nodes, setNodes, onNodesChangeRaw] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Hide inspector during drag / selection to avoid expensive re-renders
  const [interacting, setInteracting] = useState(false);
  const startInteracting = useCallback(() => setInteracting(true), []);
  const stopInteracting = useCallback(() => setInteracting(false), []);

  // Prevent deletion of Start nodes via keyboard or any other mechanism
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const onNodesChange = useCallback((changes) => {
    const filtered = changes.filter((c) => {
      if (c.type === 'remove') {
        const node = nodesRef.current.find((n) => n.id === c.id);
        if (node?.type === 'sfStart') return false;
      }
      return true;
    });
    onNodesChangeRaw(filtered);
  }, [onNodesChangeRaw]);
  const viewportRef = useRef(null);
  const hasSavedViewport = useRef(false);
  const saveTimerRef = useRef(null);
  const paneRef = useRef(null);
  const edgesRef = useRef(edges);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  // Context menu (pane and node)
  const [ctxMenu, setCtxMenu] = useState(null);
  const [nodeCtxMenu, setNodeCtxMenu] = useState(null);
  const ctxPosRef = useRef({ x: 0, y: 0 });

  // Nexus drawer
  const [nexusDrawerOpen, setNexusDrawerOpen] = useState(false);
  const [nexusContext, setNexusContext] = useState(null);

  const openNexusWithContext = useCallback((nodeOrId) => {
    const node = typeof nodeOrId === 'string' ? nodes.find((n) => n.id === nodeOrId) : nodeOrId;
    if (!node) return;
    setNexusContext({
      nodeId: node.id,
      nodeType: node.type,
      nodeData: node.data,
      chapterId: storyflowId,
      chapterName: flowData?.name,
      chapterNumber: flowData?.number,
      graphJson: { nodes, edges }
    });
    setNexusDrawerOpen(true);
  }, [nodes, edges, storyflowId, flowData]);

  // JSON dialog & minimap toggle
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [clearConfirm, setClearConfirm] = useState(false);
  const [miniMapVisible, setMiniMapVisible] = useState(false);
  const [viewportRev, setViewportRev] = useState(0);

  // AI summary loading (tracks which provider is currently loading: 'gemini' | 'grok' | null)
  const [aiLoading, setAiLoading] = useState(null);

  // Graph check — cycle through errors
  const [checkIdx, setCheckIdx] = useState(0);
  const [checkMsg, setCheckMsg] = useState(null); // { text, severity }

  // Gemini translation review
  const [geminiOpen, setGeminiOpen] = useState(false);

  // Play modal
  const [playModal, setPlayModal] = useState(null); // { src }
  const [playPresetId, setPlayPresetId] = useState('3:4');
  const [playIframeKey, setPlayIframeKey] = useState(0);
  const [playTestTier, setPlayTestTier] = useState('');
  const playTestTierInit = useRef(false);
  useEffect(() => {
    if (playTestTierInit.current) return;
    const tiers = activeTeam?.gameConfig?.patreonTiers || [];
    if (tiers.length > 0) { setPlayTestTier(tiers[tiers.length - 1]); playTestTierInit.current = true; }
  }, [activeTeam]);
  const playPreset = useMemo(() => SCREEN_PRESETS.find((p) => p.id === playPresetId) || SCREEN_PRESETS[0], [playPresetId]);

  // Storage browser modal state: { fileFilter: 'image'|'video'|'audio', nodeField: string }
  const [storageBrowser, setStorageBrowser] = useState(null);

  // Audio playback in inspector
  const [inspectorAudioPlaying, setInspectorAudioPlaying] = useState(false);
  const inspectorAudioRef = useRef(null);
  const stopInspectorAudio = useCallback(() => {
    if (inspectorAudioRef.current) {
      inspectorAudioRef.current.pause();
      inspectorAudioRef.current.src = '';
      inspectorAudioRef.current = null;
    }
    setInspectorAudioPlaying(false);
  }, []);
  const toggleInspectorAudio = useCallback((url) => {
    if (inspectorAudioPlaying) {
      stopInspectorAudio();
      return;
    }
    stopInspectorAudio();
    if (url) {
      const audio = new Audio(url);
      audio.addEventListener('ended', () => setInspectorAudioPlaying(false));
      audio.play().catch(() => {});
      inspectorAudioRef.current = audio;
      setInspectorAudioPlaying(true);
    }
  }, [inspectorAudioPlaying, stopInspectorAudio]);

  // Language toggle
  const [sfLang, setSfLang] = useState('ru');

  // AI suggestion modal
  const [suggestionPreview, setSuggestionPreview] = useState(null);
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [suggestionNodeId, setSuggestionNodeId] = useState(null);

  // AI continuation modal (pin drag → "Continue with AI")
  const [aiContinueModal, setAiContinueModal] = useState(null); // { sourceNodeId, sourceHandle, flowPos }
  const [aiContinuePrompt, setAiContinuePrompt] = useState('');
  const [aiContinueLoading, setAiContinueLoading] = useState(false);
  const [aiContinueResult, setAiContinueResult] = useState(null); // { description, nodes }

  // Node search
  const [nodeSearchOpen, setNodeSearchOpen] = useState(false);
  const [nodeSearchQuery, setNodeSearchQuery] = useState('');
  const nodeSearchRef = useRef(null);

  const nodeSearchResults = useMemo(() => {
    if (nodeSearchQuery.length < 3) return [];
    const q = nodeSearchQuery.toLowerCase();
    return nodes.filter((n) => {
      const d = n.data || {};
      const fields = [d.text, d.label, d.speakerId, d.imageName, d.videoName, d.decisionName, d.characterName, d.targetChapterName, d.statusEmoji];
      if (d.variants?.length) fields.push(...d.variants);
      if (d.options?.length) fields.push(...d.options);
      return fields.some((f) => f && String(f).toLowerCase().includes(q));
    }).slice(0, 20);
  }, [nodes, nodeSearchQuery]);

  const focusNode = useCallback((nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    const x = node.position.x + (node.measured?.width || 200) / 2;
    const y = node.position.y + (node.measured?.height || 100) / 2;
    setCenter(x, y, { zoom: 1.2, duration: 300 });
    setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === nodeId })));
    setNodeSearchOpen(false);
    setNodeSearchQuery('');
  }, [nodes, setCenter, setNodes]);

  // Actions context for SfNodes (translate, suggest)
  const sfActions = useMemo(() => ({
    translateNode: async (nodeId) => {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}/translate-node`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodeId, teamId: activeTeam?.id })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || 'Translation failed');
        return;
      }
      const { text_en, variants_en } = await res.json();
      setNodes((nds) => nds.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, text_en, variants_en } } : n
      ));
    },
    suggestDialogue: async (nodeId) => {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}/suggest-dialogue`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodeId, teamId: activeTeam?.id })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || 'AI suggestion failed');
        return;
      }
      const suggestion = await res.json();
      setSuggestionPreview(suggestion);
      setSuggestionNodeId(nodeId);
      setSuggestionModalOpen(true);
    }
  }), [getToken, storyflowId, activeTeam, setNodes]);

  const applySuggestion = useCallback(() => {
    if (!suggestionPreview || !suggestionNodeId) return;
    setNodes((nds) => nds.map((n) =>
      n.id === suggestionNodeId
        ? { ...n, data: { ...n.data, text: suggestionPreview.text, variants: suggestionPreview.variants } }
        : n
    ));
    setSuggestionModalOpen(false);
    setSuggestionPreview(null);
    setSuggestionNodeId(null);
  }, [suggestionPreview, suggestionNodeId, setNodes]);

  // AI continuation handlers
  const handleAiContinue = useCallback(async () => {
    if (!aiContinueModal || !aiContinuePrompt.trim()) return;
    setAiContinueLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}/ai-continue`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodeId: aiContinueModal.sourceNodeId,
          prompt: aiContinuePrompt.trim(),
          teamId: flowData?.teamId
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || 'AI continuation failed');
        return;
      }
      const data = await res.json();
      setAiContinueResult(data);
    } catch (e) {
      alert('AI continuation error: ' + e.message);
    } finally {
      setAiContinueLoading(false);
    }
  }, [aiContinueModal, aiContinuePrompt, getToken, storyflowId, flowData]);

  const applyAiContinuation = useCallback(() => {
    if (!aiContinueResult?.nodes?.length || !aiContinueModal) return;
    const basePos = aiContinueModal.flowPos;
    const newNodesArr = [];
    const newEdgesArr = [];

    // Fix literal \n sequences from AI output → real newlines
    const fixNewlines = (s) => (s || '').replace(/\\n/g, '\n');

    const buildNodeData = (spec) => {
      switch (spec.type) {
        case 'sfDialogue':
          return {
            label: spec.characterName || spec.speakerId || 'Dialogue',
            speakerId: spec.speakerId || '',
            text: fixNewlines(spec.text),
            text_en: '',
            variants: (spec.variants || []).filter(Boolean),
            variants_en: [],
            relationships: [],
            tiers: []
          };
        case 'sfImage':
          return { label: 'Image', imageName: spec.imageName || '', imageUrl: '', imagePreview512: '' };
        case 'sfVideo':
          return {
            label: 'Video',
            videoName: '',
            videoUrl: '',
            videoPreview512: '',
            text: fixNewlines(spec.text),
            variants: (spec.variants || []).filter(Boolean),
            variants_en: [],
            relationships: [],
            tiers: []
          };
        case 'sfTimeskip':
          return {
            label: 'Timeskip',
            timeskipMode: spec.timeskipMode || 'add',
            hours: parseInt(spec.hours) || 0,
            minutes: parseInt(spec.minutes) || 0
          };
        case 'sfIntroduce':
          return { label: 'Introduce', characterId: spec.characterId || '', characterName: spec.characterName || '' };
        case 'sfStatus':
          return { label: 'Status', characterId: spec.characterId || '', characterName: spec.characterName || '', statusEmoji: spec.statusEmoji || '' };
        case 'sfInsta':
          return { label: 'Insta Post', characterId: spec.characterId || '', characterName: spec.characterName || '', imageName: spec.imageName || '', imageUrl: '', imagePreview512: '', instaSection: spec.instaSection || 'public', caption: spec.caption || '', likes: parseInt(spec.likes) || 0 };
        case 'sfDecision':
          return { label: 'Decision', decisionName: spec.decisionName || '', options: (spec.options || []).filter(Boolean) };
        case 'sfRpSwitch':
          return { label: 'RP Switch', characterId: spec.characterId || '', characterName: spec.characterName || '', relationshipLevel: parseInt(spec.relationshipLevel) || 0 };
        case 'sfNextChapter':
          return { label: 'Next Chapter', targetChapterName: spec.targetChapterName || '' };
        default:
          return { label: spec.type || 'Node' };
      }
    };

    aiContinueResult.nodes.forEach((spec, i) => {
      const id = newNodeId();
      const position = { x: basePos.x, y: basePos.y + i * 220 };
      const data = buildNodeData(spec);
      newNodesArr.push({ id, type: spec.type, position, data, selected: false });

      if (i === 0) {
        // Connect to the source pin
        newEdgesArr.push({
          id: newEdgeId(), type: 'bleu', animated: true,
          source: aiContinueModal.sourceNodeId,
          sourceHandle: aiContinueModal.sourceHandle || 'flow-out',
          target: id, targetHandle: 'flow-in'
        });
      } else {
        // Connect to previous node
        const prevId = newNodesArr[i - 1].id;
        const prevSpec = aiContinueResult.nodes[i - 1];
        // If previous dialogue/video has variants, connect from choice-0
        let srcHandle = 'flow-out';
        if ((prevSpec.type === 'sfDialogue' || prevSpec.type === 'sfVideo') && prevSpec.variants?.length > 0) {
          srcHandle = 'choice-0';
        }
        newEdgesArr.push({
          id: newEdgeId(), type: 'bleu', animated: true,
          source: prevId, sourceHandle: srcHandle,
          target: id, targetHandle: 'flow-in'
        });
      }
    });

    setNodes((nds) => [...nds, ...newNodesArr]);
    setEdges((eds) => [...eds, ...newEdgesArr]);
    setAiContinueModal(null);
    setAiContinueResult(null);
    setAiContinuePrompt('');
  }, [aiContinueResult, aiContinueModal, setNodes, setEdges]);

  // Patreon tiers from config
  const patreonTiers = useMemo(() => activeTeam?.gameConfig?.patreonTiers || [], [activeTeam]);

  // Load storyflow
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const res = await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Storyflow not found');
        const data = await res.json();
        if (cancelled) return;
        setFlowData(data);

        const merged = mergeGraphWithMeta(data.graph, data.graphMeta);
        // Auto-add mandatory Start node if missing
        if (!merged.nodes.some((n) => n.type === 'sfStart')) {
          merged.nodes.push({
            id: newNodeId(),
            type: 'sfStart',
            position: { x: 100, y: 200 },
            data: { label: 'Start' }
          });
        }
        // Migrate old Decision/Branch data → new options array format
        const handleMap = {};
        for (const n of merged.nodes) {
          if (n.type === 'sfDecision' && !n.data.options && (n.data.optionA || n.data.optionB)) {
            n.data.options = [n.data.optionA || 'Option A', n.data.optionB || 'Option B'];
            handleMap[`${n.id}:decision-in-a`] = 'decision-in-0';
            handleMap[`${n.id}:decision-out-a`] = 'decision-out-0';
            handleMap[`${n.id}:decision-in-b`] = 'decision-in-1';
            handleMap[`${n.id}:decision-out-b`] = 'decision-out-1';
          }
          if (n.type === 'sfBranch' && !n.data.switchOptions && (n.data.branchOptionA || n.data.branchOptionB)) {
            n.data.switchOptions = [n.data.branchOptionA || 'A', n.data.branchOptionB || 'B'];
            handleMap[`${n.id}:branch-out-a`] = 'switch-out-0';
            handleMap[`${n.id}:branch-out-b`] = 'switch-out-1';
          }
        }
        if (Object.keys(handleMap).length > 0) {
          for (const e of merged.edges) {
            const th = handleMap[`${e.target}:${e.targetHandle}`];
            if (th) e.targetHandle = th;
            const sh = handleMap[`${e.source}:${e.sourceHandle}`];
            if (sh) e.sourceHandle = sh;
          }
        }
        setNodes(merged.nodes);
        setEdges(merged.edges);
        if (merged.viewport) { viewportRef.current = merged.viewport; hasSavedViewport.current = true; }

        // Load characters, storyflows, and storage images for the same team
        if (data.teamId) {
          const cRes = await fetch(`${API_BASE_URL}/api/characters?teamId=${encodeURIComponent(data.teamId)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cRes.ok) {
            const cData = await cRes.json();
            if (!cancelled) setCharacters(cData.characters || []);
          }
          // Load sibling storyflows for Next Chapter selector
          const sfRes = await fetch(`${API_BASE_URL}/api/storyflows?teamId=${encodeURIComponent(data.teamId)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (sfRes.ok) {
            const sfData = await sfRes.json();
            if (!cancelled) setTeamStoryflows((sfData.storyflows || []).filter((sf) => sf.id !== storyflowId));
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storyflowId, getToken, setNodes, setEdges]);

  // Persist (manual + auto-save)
  const persist = useCallback(async () => {
    try {
      setSaving(true);
      const token = await getToken();
      const vp = viewportRef.current;
      // Scrub orphan / invalid-handle edges right before we serialize so the
      // saved graph never carries stale references that would spam React
      // Flow warnings on next load.
      const { edges: orphansRemoved, removed } = filterOrphanEdges(edges, nodes);
      // Duplicate-output dedup: if two+ edges share (source, sourceHandle),
      // keep ONLY the last one in the array. React Flow renders the last
      // one, but the game engine walks and returns the first it finds — so
      // the visual edge in the editor and the path taken at runtime
      // disagree. Keeping the last matches what you see in the graph.
      const sourceKey = (e) => `${e.source}::${e.sourceHandle || 'flow-out'}`;
      const lastIdxByKey = new Map();
      orphansRemoved.forEach((e, i) => { lastIdxByKey.set(sourceKey(e), i); });
      const droppedDupes = [];
      const cleanedEdges = orphansRemoved.filter((e, i) => {
        const key = sourceKey(e);
        if (lastIdxByKey.get(key) === i) return true;
        droppedDupes.push({
          source: key,
          edgeId: e.id,
          target: `${e.target}::${e.targetHandle || 'flow-in'}`
        });
        return false;
      });
      if (droppedDupes.length > 0) {
        console.warn(
          `[Storyflow] Removed ${droppedDupes.length} duplicate edge(s) on save (kept the last one per output pin):`,
          droppedDupes
        );
      }
      if (removed > 0 || droppedDupes.length > 0) setEdges(cleanedEdges);
      const { graph, graphMeta } = splitGraphMeta(nodes, cleanedEdges, vp);
      await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ graph, graphMeta })
      });
    } catch (e) {
      console.error('Storyflow save error:', e);
    } finally {
      setSaving(false);
    }
  }, [storyflowId, getToken, nodes, edges, setEdges]);

  // Auto-save on changes
  useEffect(() => {
    if (!loading && flowData) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => persist(), 2000);
    }
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [nodes, edges, loading, flowData, persist]);

  // Ref to the edge currently being reconnected via its endpoint drag.
  const reconnectingEdgeRef = useRef(null);

  // No self-loops. Single-output-per-pin is enforced in onConnect by
  // replacing any existing edge on that (source, sourceHandle), so
  // isValidConnection stays permissive and never blocks reroutes.
  const isValidConnection = useCallback((conn) => {
    if (conn.source === conn.target) return false;
    return true;
  }, []);

  const onConnect = useCallback((params) => {
    setEdges((eds) => {
      // Replace semantics: drop any existing edge from the same output pin
      // before adding the new one. Lets the user drag out of an already
      // connected pin to reroute without manually deleting first.
      const withoutOld = eds.filter((e) =>
        !(e.source === params.source && e.sourceHandle === params.sourceHandle)
      );
      return addEdge({ ...params, id: newEdgeId(), type: 'bleu', animated: true }, withoutOld);
    });
  }, [setEdges]);

  const onReconnectStart = useCallback((_, edge) => {
    reconnectingEdgeRef.current = edge;
  }, []);

  const onReconnect = useCallback((oldEdge, newConnection) => {
    reconnectingEdgeRef.current = null;
    setEdges((eds) => {
      // Remove any OTHER edge occupying the new source handle too, so the
      // reconnected edge always wins the slot.
      const cleared = eds.filter((e) =>
        e.id === oldEdge.id ||
        !(e.source === newConnection.source && e.sourceHandle === newConnection.sourceHandle)
      );
      return reconnectEdge(oldEdge, newConnection, cleared);
    });
  }, [setEdges]);

  const onReconnectEnd = useCallback(() => {
    reconnectingEdgeRef.current = null;
  }, []);

  const onMoveEnd = useCallback((_, vp) => {
    viewportRef.current = vp;
    setViewportRev((r) => r + 1);
  }, []);

  const jsonPreview = useMemo(() => {
    void viewportRev;
    const vp = getViewport();
    const { graph, graphMeta } = splitGraphMeta(nodes, edges, vp);
    return JSON.stringify({ graph, graphMeta }, null, 2);
  }, [nodes, edges, getViewport, viewportRev]);

  // ── Gemini review: build graph object (server expects parsed JSON, not string) ──
  const buildGraphJsonForGemini = useCallback(() => {
    const vp = getViewport();
    const { graph, graphMeta } = splitGraphMeta(nodes, edges, vp);
    return { graph, graphMeta };
  }, [nodes, edges, getViewport]);

  // ── Gemini review: apply checked proposals with strict before-text verification ──
  // Computed against nodesRef.current (live state) BEFORE calling setNodes so that
  // React StrictMode's double-invocation of the updater can't double-count.
  const applyGeminiProposals = useCallback((accepted) => {
    if (!Array.isArray(accepted) || accepted.length === 0) {
      return { applied: 0, skipped: [] };
    }
    const liveNodes = nodesRef.current || [];
    const byId = new Map();
    liveNodes.forEach((n) => byId.set(n.id, n));

    const groups = new Map();
    for (const p of accepted) {
      if (!groups.has(p.nodeId)) groups.set(p.nodeId, []);
      groups.get(p.nodeId).push(p);
    }

    const skipped = [];
    let appliedCount = 0;
    const patched = new Map(); // nodeId -> new data

    for (const [nodeId, props] of groups.entries()) {
      const node = byId.get(nodeId);
      if (!node) {
        for (const p of props) skipped.push({ proposal: p, reason: 'node not in graph' });
        continue;
      }
      if (node.type !== 'sfDialogue') {
        for (const p of props) skipped.push({ proposal: p, reason: 'node is not sfDialogue' });
        continue;
      }
      let data = { ...node.data };
      let mutated = false;
      for (const p of props) {
        let didSomething = false;
        if (p.field === 'text') {
          if (typeof p.ruAfter === 'string' && (data.text ?? '') === p.ruBefore) {
            data = { ...data, text: p.ruAfter };
            didSomething = true;
          }
          if (typeof p.enAfter === 'string' && (data.text_en ?? '') === p.enBefore) {
            data = { ...data, text_en: p.enAfter };
            didSomething = true;
          }
        } else if (p.field === 'variant') {
          const i = p.variantIndex;
          if (typeof i === 'number' && Number.isInteger(i) && i >= 0) {
            if (typeof p.ruAfter === 'string') {
              const arr = Array.isArray(data.variants) ? data.variants : [];
              if (i < arr.length && (arr[i] ?? '') === p.ruBefore) {
                const next = arr.slice();
                next[i] = p.ruAfter;
                data = { ...data, variants: next };
                didSomething = true;
              }
            }
            if (typeof p.enAfter === 'string') {
              const arr = Array.isArray(data.variants_en) ? data.variants_en : [];
              if (i < arr.length && (arr[i] ?? '') === p.enBefore) {
                const next = arr.slice();
                next[i] = p.enAfter;
                data = { ...data, variants_en: next };
                didSomething = true;
              }
            }
          }
        }
        if (didSomething) {
          mutated = true;
          appliedCount += 1;
        } else {
          skipped.push({ proposal: p, reason: 'current "before" text does not match the graph' });
        }
      }
      if (mutated) patched.set(nodeId, data);
    }

    if (patched.size > 0) {
      setNodes((nds) => nds.map((n) => (patched.has(n.id) ? { ...n, data: patched.get(n.id) } : n)));
    }
    return { applied: appliedCount, skipped };
  }, [setNodes]);

  // ── Graph check: find errors and focus viewport on them ──
  const findGraphErrors = useCallback(() => {
    const errors = [];
    const incomingCount = new Map();
    const outgoingByHandle = new Map(); // nodeId::handle -> count
    const outgoingCount = new Map();
    for (const e of edges) {
      incomingCount.set(e.target, (incomingCount.get(e.target) || 0) + 1);
      outgoingCount.set(e.source, (outgoingCount.get(e.source) || 0) + 1);
      const hk = `${e.source}::${e.sourceHandle || 'flow-out'}`;
      outgoingByHandle.set(hk, (outgoingByHandle.get(hk) || 0) + 1);
    }

    for (const n of nodes) {
      const d = n.data || {};
      const inc = incomingCount.get(n.id) || 0;
      const outc = outgoingCount.get(n.id) || 0;

      // 1. Node with input pins has no incoming connections (skip sfStart)
      if (n.type !== 'sfStart' && inc === 0) {
        errors.push({ nodeId: n.id, msg: 'No incoming connection' });
      }

      // 2. Decision node: input count != output count
      if (n.type === 'sfDecision') {
        const opts = d.options || [];
        const outPins = opts.reduce((s, _, i) => s + (outgoingByHandle.get(`${n.id}::decision-out-${i}`) ? 1 : 0), 0);
        const inPinsConnected = opts.reduce((s, _, i) => {
          return s + (edges.some(e => e.target === n.id && e.targetHandle === `decision-in-${i}`) ? 1 : 0);
        }, 0);
        if (inPinsConnected !== outPins && opts.length > 0) {
          errors.push({ nodeId: n.id, msg: `Decision: ${inPinsConnected} inputs vs ${outPins} outputs` });
        }
      }

      // 3. Dialogue/Video with answer variants missing connections
      if (n.type === 'sfDialogue' || n.type === 'sfVideo') {
        const variants = d.variants || [];
        if (variants.length > 0) {
          for (let i = 0; i < variants.length; i++) {
            if (!outgoingByHandle.get(`${n.id}::choice-${i}`)) {
              errors.push({ nodeId: n.id, msg: `Answer ${i} has no connection` });
              break;
            }
          }
        }
      }

      // 4. Node that should have output but has none
      const noOutputTypes = ['sfNextChapter']; // these are terminals
      if (!noOutputTypes.includes(n.type) && n.type !== 'sfStart') {
        // Check if node type normally has outputs
        const hasVariants = (n.type === 'sfDialogue' || n.type === 'sfVideo') && (d.variants?.length > 0);
        const hasSwitch = n.type === 'sfBranch' && d.switchOptions?.length > 0;
        const hasDecision = n.type === 'sfDecision' && d.options?.length > 0;
        const isSimple = !hasVariants && !hasSwitch && !hasDecision;
        if (isSimple && outc === 0) {
          errors.push({ nodeId: n.id, msg: 'No outgoing connection' });
        }
      }

      // 5. Text on one language but not the other
      // Skip if the text is only emoji, symbols, digits, punctuation (universal, no translation needed)
      if (n.type === 'sfDialogue' || n.type === 'sfVideo') {
        const isUniversal = (s) => /^[\s\d\p{Emoji_Presentation}\p{Emoji}\u200d\ufe0f\p{P}\p{S}]*$/u.test(s);
        const hasRu = !!(d.text || '').trim();
        const hasEn = !!(d.text_en || '').trim();
        if (hasRu && !hasEn && !isUniversal((d.text || '').trim())) errors.push({ nodeId: n.id, msg: 'Has RU text but no EN text' });
        if (hasEn && !hasRu && !isUniversal((d.text_en || '').trim())) errors.push({ nodeId: n.id, msg: 'Has EN text but no RU text' });
        const vRu = d.variants || [];
        const vEn = d.variants_en || [];
        const maxV = Math.max(vRu.length, vEn.length);
        for (let i = 0; i < maxV; i++) {
          const rV = (vRu[i] || '').trim();
          const eV = (vEn[i] || '').trim();
          if (rV && !eV && !isUniversal(rV)) { errors.push({ nodeId: n.id, msg: `Answer ${i}: has RU but no EN` }); break; }
          if (eV && !rV && !isUniversal(eV)) { errors.push({ nodeId: n.id, msg: `Answer ${i}: has EN but no RU` }); break; }
        }
      }

      // 6. Missing required fields by node type
      if (n.type === 'sfImage' && !d.imageName && !d.imageUrl) errors.push({ nodeId: n.id, msg: 'No image selected' });
      if (n.type === 'sfVideo' && !d.videoUrl && !d.videoName) errors.push({ nodeId: n.id, msg: 'No video selected' });
      if (n.type === 'sfNextChapter' && !d.targetChapterName && !d.targetStoryflowId) errors.push({ nodeId: n.id, msg: 'No target chapter' });
      if (n.type === 'sfBranch' && !d.decisionRef) errors.push({ nodeId: n.id, msg: 'No decision reference' });
      if (n.type === 'sfDecision' && !d.decisionName) errors.push({ nodeId: n.id, msg: 'No decision name' });
      if (n.type === 'sfDecision' && (d.options || []).length < 2) errors.push({ nodeId: n.id, msg: 'Decision needs at least 2 options' });
      if (n.type === 'sfIntroduce' && !d.characterId && !d.characterName) errors.push({ nodeId: n.id, msg: 'No character selected' });
      if (n.type === 'sfRpSwitch' && !d.characterId && !d.characterName) errors.push({ nodeId: n.id, msg: 'No character selected' });
      if (n.type === 'sfStatus' && !d.characterId && !d.characterName) errors.push({ nodeId: n.id, msg: 'No character selected' });
      if (n.type === 'sfInsta' && !d.characterId && !d.characterName) errors.push({ nodeId: n.id, msg: 'No character selected' });
      if (n.type === 'sfInsta' && !d.imageName && !d.imageUrl) errors.push({ nodeId: n.id, msg: 'No image selected' });

      // 7. Cyrillic in English text
      if (n.type === 'sfDialogue' || n.type === 'sfVideo') {
        if (d.text_en && CYRILLIC_RE.test(d.text_en)) errors.push({ nodeId: n.id, msg: 'Cyrillic in EN text' });
        const vEn = d.variants_en || [];
        for (let i = 0; i < vEn.length; i++) {
          if (vEn[i] && CYRILLIC_RE.test(vEn[i])) { errors.push({ nodeId: n.id, msg: `Cyrillic in EN answer ${i}` }); break; }
        }
      }

      // 8. Timeskip with 0/0 and mode skip
      if (n.type === 'sfTimeskip' && d.timeskipMode === 'skip' && !d.hours && !d.minutes) {
        errors.push({ nodeId: n.id, msg: 'Timeskip skip 0h 0m' });
      }
    }

    return errors;
  }, [nodes, edges]);

  const checkGraph = useCallback(() => {
    const errors = findGraphErrors();
    if (errors.length === 0) {
      setCheckMsg({ text: 'No errors found', severity: 'success' });
      setCheckIdx(0);
      return;
    }
    const idx = checkIdx >= errors.length ? 0 : checkIdx;
    const err = errors[idx];
    const node = nodes.find((n) => n.id === err.nodeId);
    if (node) {
      const x = node.position?.x ?? node.positionAbsolute?.x ?? 0;
      const y = node.position?.y ?? node.positionAbsolute?.y ?? 0;
      const w = node.measured?.width || 280;
      const h = node.measured?.height || 100;
      setCenter(x + w / 2, y + h / 2, { zoom: 1, duration: 300 });
      setNodes((nds) => nds.map((n) => ({ ...n, selected: n.id === err.nodeId })));
    }
    setCheckMsg({ text: `[${idx + 1}/${errors.length}] ${err.msg}`, severity: 'warning' });
    setCheckIdx(idx + 1);
  }, [findGraphErrors, checkIdx, nodes, setNodes, setCenter]);

  // Auto-layout: horizontal left-to-right, branches fan out vertically
  const autoLayoutNodes = useCallback(() => {
    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const outgoing = new Map();
    const incomingMap = new Map(); // nodeId -> [sourceIds]
    for (const e of edges) {
      if (!outgoing.has(e.source)) outgoing.set(e.source, []);
      outgoing.get(e.source).push(e);
      if (!incomingMap.has(e.target)) incomingMap.set(e.target, []);
      incomingMap.get(e.target).push(e.source);
    }

    const H = 400;
    const V = 180;

    const startNode = nodes.find((n) => n.type === 'sfStart') || nodes[0];
    if (!startNode) return;

    // Pass 1: assign column = longest path from start (ensures edges go right)
    const col = new Map();
    const visited = new Set();

    // Topological-ish BFS: process a node only when all its parents are assigned
    // (or on second pass for cycles)
    col.set(startNode.id, 0);
    visited.add(startNode.id);
    const queue = [startNode.id];
    const deferred = []; // nodes whose parents aren't all assigned yet

    let safety = nodes.length * 3;
    while ((queue.length > 0 || deferred.length > 0) && safety-- > 0) {
      let id;
      if (queue.length > 0) {
        id = queue.shift();
      } else {
        // Break cycle: take first deferred, assign based on known parents
        id = deferred.shift();
        if (visited.has(id)) continue;
        const parents = (incomingMap.get(id) || []).filter((p) => col.has(p));
        const maxParentCol = parents.length > 0 ? Math.max(...parents.map((p) => col.get(p))) : 0;
        col.set(id, maxParentCol + 1);
        visited.add(id);
      }

      const outs = outgoing.get(id) || [];
      const children = [...new Set(outs.map((e) => e.target))].filter((t) => nodeById.has(t));

      for (const cid of children) {
        if (visited.has(cid)) {
          // Already placed — push right if needed (no back-edges)
          const minCol = col.get(id) + 1;
          if (col.get(cid) < minCol) {
            // Would create back-edge; push the target forward
            // But only if it won't create a cycle (check if target can reach source)
            // Simple heuristic: just push it
            col.set(cid, minCol);
          }
          continue;
        }

        // Check if all parents are assigned
        const parents = (incomingMap.get(cid) || []).filter((p) => nodeById.has(p));
        const allParentsReady = parents.every((p) => col.has(p));

        if (allParentsReady) {
          const maxParentCol = parents.length > 0 ? Math.max(...parents.map((p) => col.get(p))) : col.get(id);
          col.set(cid, maxParentCol + 1);
          visited.add(cid);
          queue.push(cid);
        } else {
          deferred.push(cid);
        }
      }
    }

    // Pass 2: assign rows — position near average of parents, then resolve overlaps
    const row = new Map();
    const sorted = [...visited].sort((a, b) => (col.get(a) || 0) - (col.get(b) || 0));

    // First: desired row = average of parent rows (or 0 for root)
    for (const id of sorted) {
      const parents = (incomingMap.get(id) || []).filter((p) => row.has(p));
      if (parents.length > 0) {
        const avg = parents.reduce((s, p) => s + row.get(p), 0) / parents.length;
        row.set(id, avg);
      } else {
        row.set(id, 0);
      }
    }

    // Then: resolve overlaps within each column using actual node heights
    const GAP = 30; // px gap between nodes
    const byCol = new Map();
    for (const id of visited) {
      const c = col.get(id) || 0;
      if (!byCol.has(c)) byCol.set(c, []);
      byCol.get(c).push(id);
    }
    // Convert row units to px, resolve overlaps using real heights
    // First pass: compute raw Y from row * V
    const yPos = new Map();
    for (const id of visited) yPos.set(id, row.get(id) * V);

    for (const [, ids] of byCol) {
      ids.sort((a, b) => yPos.get(a) - yPos.get(b));
      for (let i = 1; i < ids.length; i++) {
        const prevId = ids[i - 1];
        const currId = ids[i];
        const prevNode = nodeById.get(prevId);
        const prevH = prevNode?.measured?.height || prevNode?.height || 120;
        const minY = yPos.get(prevId) + prevH + GAP;
        if (yPos.get(currId) < minY) {
          yPos.set(currId, minY);
        }
      }
    }

    // Unvisited nodes at the end
    let maxCol = Math.max(0, ...col.values()) + 1;
    const maxY = Math.max(0, ...yPos.values());
    let extraY = maxY + V + 50;
    for (const n of nodes) {
      if (!visited.has(n.id)) {
        col.set(n.id, maxCol);
        yPos.set(n.id, extraY);
        extraY += (n.measured?.height || 120) + GAP;
      }
    }

    setNodes((nds) =>
      nds.map((n) => ({
        ...n,
        position: {
          x: (col.get(n.id) || 0) * H + 50,
          y: (yPos.get(n.id) || 0) + 50
        }
      }))
    );
  }, [nodes, edges, setNodes]);

  // Palette click: add node at center of canvas
  const addFromPaletteClick = useCallback(
    (type, defaultData) => {
      const el = paneRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const flowPos = screenToFlowPosition({
        x: r.left + r.width / 2,
        y: r.top + r.height / 2
      });
      const id = newNodeId();
      const newNode = {
        id,
        type,
        position: { x: flowPos.x - 128, y: flowPos.y - 40 },
        data: { ...defaultData },
        selected: true
      };
      setNodes((nds) => {
        if (type === 'sfStart' && nds.some((n) => n.type === 'sfStart')) return nds;
        return nds.map((n) => ({ ...n, selected: false })).concat(newNode);
      });
    },
    [screenToFlowPosition, setNodes]
  );

  // Palette drag-and-drop
  const SF_DND_TYPE = 'application/sfNodeType';

  const addFromPaletteDragStart = useCallback((event, type, defaultData) => {
    event.dataTransfer.setData(SF_DND_TYPE, JSON.stringify({ type, defaultData }));
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();

      // Handle JSON file drop — replaces entire graph
      const files = e.dataTransfer.files;
      if (files && files.length === 1 && files[0].name.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          try {
            const parsed = JSON.parse(ev.target.result);
            const g = parsed.graph || parsed;
            const gm = parsed.graphMeta || { positions: {}, viewport: null };
            const merged = mergeGraphWithMeta(g, gm);
            setNodes(merged.nodes);
            setEdges(merged.edges);
            if (merged.viewport) {
              viewportRef.current = merged.viewport;
              hasSavedViewport.current = true;
            }
          } catch (err) {
            console.error('JSON import error:', err);
          }
        };
        reader.readAsText(files[0]);
        return;
      }

      // Handle palette node DnD
      const raw = e.dataTransfer.getData(SF_DND_TYPE);
      if (!raw) return;
      try {
        const { type, defaultData } = JSON.parse(raw);
        const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const id = newNodeId();
        const newNode = {
          id,
          type,
          position: { x: flowPos.x - 128, y: flowPos.y - 40 },
          data: { ...defaultData },
          selected: true
        };
        setNodes((nds) => {
          if (type === 'sfStart' && nds.some((n) => n.type === 'sfStart')) return nds;
          return nds.map((n) => ({ ...n, selected: false })).concat(newNode);
        });
      } catch { /* ignore */ }
    },
    [screenToFlowPosition, setNodes, setEdges]
  );

  // Context menu
  const onPaneContextMenu = useCallback((e) => {
    e.preventDefault();
    const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    ctxPosRef.current = flowPos;
    setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, flowPos, connectFrom: null });
  }, [screenToFlowPosition]);

  // Show context menu when edge is dropped on empty space
  const onConnectEnd = useCallback(
    (event, connectionState) => {
      if (!connectionState.fromNode || connectionState.toNode) return;
      const clientX = 'changedTouches' in event ? event.changedTouches[0].clientX : event.clientX;
      const clientY = 'changedTouches' in event ? event.changedTouches[0].clientY : event.clientY;
      const flowPos = screenToFlowPosition({ x: clientX, y: clientY });
      const handleId = connectionState.fromHandle?.id ?? 'flow-out';
      const handleType = connectionState.fromHandle?.type ?? 'source';
      ctxPosRef.current = flowPos;
      setCtxMenu({
        mouseX: clientX,
        mouseY: clientY,
        flowPos,
        connectFrom: { nodeId: connectionState.fromNode.id, handleId, handleType }
      });
    },
    [screenToFlowPosition]
  );

  const closeCtx = () => { setCtxMenu(null); setNodeCtxMenu(null); };

  const cleanupOrphanEdges = useCallback(() => {
    setEdges((eds) => {
      const { edges: kept, removed } = filterOrphanEdges(eds, nodesRef.current || []);
      return removed > 0 ? kept : eds;
    });
  }, [setEdges]);

  // Run cleanup once after initial load.
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => { cleanupOrphanEdges(); }, 200);
    return () => clearTimeout(t);
  }, [loading, cleanupOrphanEdges]);

  // Node context menu
  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault();
    e.stopPropagation();
    setNodeCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, nodeId: node.id });
  }, []);

  // Selection context menu (when a group is selected via selection box)
  const onSelectionContextMenu = useCallback((e, selectedNodes) => {
    e.preventDefault();
    e.stopPropagation();
    const first = selectedNodes?.[0];
    if (!first) return;
    setNodeCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, nodeId: first.id });
  }, []);

  // ── Copy / paste of selected nodes ─────────────────────────────
  const sfPastePosRef = useRef({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      try {
        sfPastePosRef.current = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [screenToFlowPosition]);

  const copySelection = useCallback((focusNodeId = null) => {
    let selected = (nodesRef.current || []).filter((n) => n.selected);
    if (focusNodeId) {
      const already = selected.some((n) => n.id === focusNodeId);
      if (!already) {
        const one = (nodesRef.current || []).find((n) => n.id === focusNodeId);
        if (one) selected = [one];
      }
    }
    if (!selected.length) return;
    const ids = new Set(selected.map((n) => n.id));
    const subEdges = (edgesRef.current || []).filter((e) => ids.has(e.source) && ids.has(e.target));
    const payload = { v: 1, nodes: selected, edges: subEdges };
    try {
      sessionStorage.setItem('flowcore_sf_clipboard', JSON.stringify(payload));
    } catch (_) { /* ignore */ }
  }, []);

  // Live ref to characters so the Lorema paste handler stays stable
  // regardless of how many times the character list re-fetches.
  const charactersRef = useRef(characters);
  useEffect(() => { charactersRef.current = characters; }, [characters]);

  // Lorema clipboard has first priority on Ctrl+V when a lorema-flow-clipboard
  // JSON is detected in the system clipboard. Returns true if it handled the
  // paste, false otherwise (so the caller can fall through to the internal
  // sessionStorage clipboard).
  const pasteLoremaClipboard = useCallback((rawText, atFlowPos = null) => {
    const payload = parseLoremaClipboardText(rawText);
    if (!payload) return false;
    const target = atFlowPos || sfPastePosRef.current;
    const { nodes: newNodesArr, edges: newEdgesArr } = importLoremaClipboard(payload, {
      characters: charactersRef.current,
      targetFlowPos: target
    });
    if (!newNodesArr.length) return false;
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })).concat(newNodesArr));
    const newIds = newNodesArr.map((n) => n.id);
    // Same deferred-edge pattern as pasteFromClipboard — dynamic handle types
    // need two rAFs + updateNodeInternals before edges can resolve.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try { updateNodeInternals(newIds); } catch (_) { /* ignore */ }
        setEdges((eds) => eds.concat(newEdgesArr));
      });
    });
    return true;
  }, [setNodes, setEdges, updateNodeInternals]);

  const pasteFromClipboard = useCallback((atFlowPos = null) => {
    let raw;
    try { raw = sessionStorage.getItem('flowcore_sf_clipboard'); } catch (_) { return; }
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); } catch (_) { return; }
    if (!payload?.nodes?.length) return;
    const clipNodes = payload.nodes;
    const clipEdges = payload.edges || [];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of clipNodes) {
      const x = n.position?.x ?? 0;
      const y = n.position?.y ?? 0;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + 220);
      maxY = Math.max(maxY, y + 100);
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const target = atFlowPos || sfPastePosRef.current;
    const dx = (target?.x ?? cx) - cx;
    const dy = (target?.y ?? cy) - cy;
    const idMap = new Map();
    for (const n of clipNodes) idMap.set(n.id, newNodeId());
    const newNodesArr = clipNodes.map((n) => {
      const clone = JSON.parse(JSON.stringify(n));
      delete clone.measured;
      delete clone.handleBounds;
      delete clone.dragging;
      delete clone.resizing;
      delete clone.width;
      delete clone.height;
      delete clone.positionAbsolute;
      clone.id = idMap.get(n.id);
      clone.position = { x: (n.position?.x ?? 0) + dx, y: (n.position?.y ?? 0) + dy };
      clone.selected = true;
      return clone;
    });
    const newEdgesArr = clipEdges
      .filter((e) => idMap.has(e.source) && idMap.has(e.target))
      .map((e) => {
        const clone = JSON.parse(JSON.stringify(e));
        clone.id = newEdgeId();
        clone.source = idMap.get(e.source);
        clone.target = idMap.get(e.target);
        return clone;
      });
    setNodes((nds) => nds.map((n) => ({ ...n, selected: false })).concat(newNodesArr));
    // Defer edges until after the new nodes have mounted, measured, and
    // registered their Handle positions in the React Flow store. Two rAFs +
    // an explicit updateNodeInternals is what finally makes React Flow stop
    // complaining about missing handles on dynamic-handle nodes (dialogue
    // variants, etc.).
    const newIds = newNodesArr.map((n) => n.id);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try { updateNodeInternals(newIds); } catch (_) { /* ignore */ }
        setEdges((eds) => eds.concat(newEdgesArr));
      });
    });
    try { sessionStorage.removeItem('flowcore_sf_clipboard'); } catch (_) { /* ignore */ }
  }, [setNodes, setEdges, updateNodeInternals]);

  useEffect(() => {
    const isInField = (target) => {
      const tag = target?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
    };
    const onCopyEvt = (e) => {
      if (isInField(e.target)) return;
      const sel = typeof window !== 'undefined' ? window.getSelection?.() : null;
      if (sel && sel.toString && sel.toString().length > 0) return;
      copySelection();
    };
    const onPasteEvt = (e) => {
      if (isInField(e.target)) return;
      // Lorema legacy importer gets first crack — if the system clipboard
      // contains a lorema-flow-clipboard JSON, route through it. Otherwise
      // fall back to our internal sessionStorage clipboard.
      const rawText = e.clipboardData?.getData?.('text') || '';
      if (rawText && pasteLoremaClipboard(rawText)) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      pasteFromClipboard();
    };
    document.addEventListener('copy', onCopyEvt);
    document.addEventListener('paste', onPasteEvt);
    return () => {
      document.removeEventListener('copy', onCopyEvt);
      document.removeEventListener('paste', onPasteEvt);
    };
  }, [copySelection, pasteFromClipboard, pasteLoremaClipboard]);

  // Play From Here: save graph then open fullscreen modal with game iframe
  const playFromHere = useCallback(async (nodeId) => {
    setNodeCtxMenu(null);
    try {
      setSaving(true);
      const token = await getToken();
      const { graph, graphMeta } = splitGraphMeta(nodes, edges, viewportRef.current);
      await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ graph, graphMeta })
      });
      const teamId = flowData?.teamId || '';
      const params = new URLSearchParams();
      if (teamId) params.set('teamId', teamId);
      params.set('entryStoryflow', storyflowId);
      params.set('entryNode', nodeId);
      if (playTestTier) params.set('testTier', playTestTier);
      setPlayModal({ src: `/game?${params.toString()}` });
      setPlayIframeKey((k) => k + 1);
    } catch (e) {
      console.error('Play From Here save error:', e);
    } finally {
      setSaving(false);
    }
  }, [storyflowId, getToken, nodes, edges, flowData, playTestTier]);

  // ── Decisions from API ──
  const [allDecisions, setAllDecisions] = useState([]);
  const loadDecisions = useCallback(async () => {
    if (!flowData?.teamId) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/decisions?teamId=${encodeURIComponent(flowData.teamId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllDecisions(data.decisions || []);
      }
    } catch (_) {}
  }, [flowData?.teamId, getToken]);

  useEffect(() => { loadDecisions(); }, [loadDecisions]);

  const prevChapterDecisions = useMemo(() => {
    const currentNum = flowData?.number ?? 0;
    return allDecisions.filter((d) => d.chapterNumber < currentNum);
  }, [allDecisions, flowData?.number]);

  const switchDecisions = useMemo(() => {
    const currentNum = flowData?.number ?? 0;
    return allDecisions.filter((d) => d.chapterNumber <= currentNum);
  }, [allDecisions, flowData?.number]);

  // Sync decision node to DB on any change
  const syncDecisionNode = useCallback(async (node) => {
    if (!node || node.type !== 'sfDecision' || !flowData) return;
    try {
      const token = await getToken();
      await fetch(`${API_BASE_URL}/api/decisions/sync`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId: flowData.teamId,
          storyflowId: storyflowId,
          nodeId: node.id,
          chapterNumber: flowData.number ?? 0,
          chapterName: flowData.name || '',
          decisionName: node.data?.decisionName || '',
          options: node.data?.options || []
        })
      });
      loadDecisions();
    } catch (_) {}
  }, [flowData, storyflowId, getToken, loadDecisions]);

  const addNodeFromCtx = useCallback((type, defaultData) => {
    if (!ctxMenu) return;
    const id = newNodeId();
    const flowPos = ctxMenu.flowPos || ctxPosRef.current;
    // Approximate node width per type (matches SfNodeCard width= values in SfNodes.js)
    const NODE_WIDTH_BY_TYPE = {
      sfDialogue: 320, sfVideo: 320, sfImage: 220, sfNextChapter: 220,
      sfDecision: 240, sfBranch: 220, sfStart: 280, sfRpSwitch: 240,
      sfTimeskip: 220, sfIntroduce: 220, sfStatus: 220, sfInsta: 220,
      sfNextFlow: 220
    };
    const width = NODE_WIDTH_BY_TYPE[type] ?? 220;
    // Pins on most storyflow nodes sit just under the header (~28-36px from
    // node top). Shift the new node so the pin we're about to connect
    // lands close to where the user released the cursor.
    const PIN_Y_OFFSET = 32;
    const fromType = ctxMenu.connectFrom?.handleType;
    let position;
    if (ctxMenu.connectFrom) {
      if (fromType === 'target') {
        // Dragging from an input pin → new node is the source, its flow-out
        // sits on the right edge. Anchor the right edge to flowPos.
        position = { x: flowPos.x - width + 4, y: flowPos.y - PIN_Y_OFFSET };
      } else {
        // Dragging from an output pin → new node is the target, its flow-in
        // sits on the left edge. Anchor the left edge to flowPos.
        position = { x: flowPos.x - 4, y: flowPos.y - PIN_Y_OFFSET };
      }
    } else {
      // Plain palette drop — center the node on the click point.
      position = { x: flowPos.x - width / 2, y: flowPos.y - 40 };
    }
    const newNode = {
      id,
      type,
      position,
      data: { ...defaultData },
      selected: true
    };
    setNodes((nds) => {
      // Only one Start node allowed per graph
      if (type === 'sfStart' && nds.some((n) => n.type === 'sfStart')) return nds;
      return nds.map((n) => ({ ...n, selected: false })).concat(newNode);
    });
    // Auto-connect if dropped from an edge
    if (ctxMenu.connectFrom) {
      const fromNodeId = ctxMenu.connectFrom.nodeId;
      const fromHandleId = ctxMenu.connectFrom.handleId;
      const isFromSource = fromType !== 'target';
      setEdges((eds) => {
        const edge = isFromSource
          ? {
              source: fromNodeId,
              sourceHandle: fromHandleId || 'flow-out',
              target: id,
              targetHandle: 'flow-in'
            }
          : {
              source: id,
              sourceHandle: 'flow-out',
              target: fromNodeId,
              targetHandle: fromHandleId || 'flow-in'
            };
        // Replace any existing edge that occupies the source pin.
        const withoutOld = eds.filter((e) =>
          !(e.source === edge.source && e.sourceHandle === edge.sourceHandle)
        );
        return withoutOld.concat({ id: newEdgeId(), type: 'bleu', animated: true, ...edge });
      });
    }
    setCtxMenu(null);
    if (type === 'sfDecision') {
      setTimeout(() => syncDecisionNode(newNode), 100);
    }
  }, [ctxMenu, setNodes, setEdges, syncDecisionNode]);

  // Inspector – selected node editing
  const selectedNodes = useMemo(() => nodes.filter((n) => n.selected), [nodes]);
  const primarySelected = selectedNodes.length === 1 ? selectedNodes[0] : null;

  // Stop inspector audio when node selection changes
  useEffect(() => { stopInspectorAudio(); }, [primarySelected?.id, stopInspectorAudio]);

  const updateSelectedData = useCallback(
    (patch) => {
      if (!primarySelected) return;
      setNodes((nds) => {
        const updated = nds.map((n) => (n.id === primarySelected.id ? { ...n, data: { ...n.data, ...patch } } : n));
        // Sync decision node to DB
        if (primarySelected.type === 'sfDecision') {
          const node = updated.find((n) => n.id === primarySelected.id);
          if (node) setTimeout(() => syncDecisionNode(node), 0);
        }
        return updated;
      });
    },
    [primarySelected, setNodes, syncDecisionNode]
  );

  // AI summary for Start node
  const loadAiSummary = useCallback(async (provider = 'gemini') => {
    if (!primarySelected || primarySelected.type !== 'sfStart' || !flowData) return;
    setAiLoading(provider);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/api/storyflows/${encodeURIComponent(storyflowId)}/ai-summary?provider=${encodeURIComponent(provider)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to generate summary');
      const data = await res.json();
      const sum = data.summary;
      updateSelectedData({
        previousSummary: typeof sum === 'object' ? (sum.ru || '') : (sum || ''),
        previousSummary_en: typeof sum === 'object' ? (sum.en || '') : '',
        accumulatedDecisions: data.accumulatedDecisions || []
      });
    } catch (e) {
      console.error('AI summary error:', e);
    } finally {
      setAiLoading(null);
    }
  }, [primarySelected, flowData, storyflowId, getToken, updateSelectedData]);

  // Update Start node's decision count when prevChapterDecisions changes
  useEffect(() => {
    const count = prevChapterDecisions.length;
    setNodes((nds) => nds.map((n) =>
      n.type === 'sfStart' && (n.data?.pastDecisionCount ?? -1) !== count
        ? { ...n, data: { ...n.data, pastDecisionCount: count } }
        : n
    ));
  }, [prevChapterDecisions.length, setNodes]);

  const characterPaletteItems = useMemo(() => buildCharacterPaletteItems(characters), [characters]);

  if (loading) {
    return <StoryflowLoader label="Loading storyflow" />;
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: '#09090b', gap: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>Go back</Button>
      </Box>
    );
  }

  return (
    <Box className="wf-editor-root" sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', bgcolor: '#09090b' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1,
        borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
        bgcolor: '#18181b'
      }}>
        <IconButton size="small" onClick={() => navigate('/?nav=storyflow')} sx={{ color: 'text.secondary' }}>
          <ArrowLeft size={18} />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }} noWrap>
          {flowData?.number === 0 ? 'Backstory — ' : flowData?.number != null ? `Ch. ${flowData.number} — ` : ''}{flowData?.name || 'Chapter'}
        </Typography>
        <Box sx={{ flex: 1 }} />

        {/* Language switcher tabs — centered */}
        <Box sx={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
          {[{ key: 'ru', flagClass: 'fi fi-ru', label: 'RU' }, { key: 'en', flagClass: 'fi fi-gb', label: 'EN' }].map(({ key, flagClass, label }) => (
            <Box
              key={key}
              onClick={() => setSfLang(key)}
              sx={{
                px: 1.5, py: 0.5, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 0.5,
                fontSize: '0.75rem', fontWeight: 600,
                color: sfLang === key ? '#a78bfa' : 'rgba(161,161,170,0.6)',
                bgcolor: sfLang === key ? 'rgba(139,92,246,0.12)' : 'transparent',
                '&:hover': { bgcolor: sfLang === key ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.04)' },
                transition: 'all 0.15s'
              }}
            >
              <span className={flagClass} style={{ fontSize: '0.7rem', lineHeight: 1 }} /> {label}
            </Box>
          ))}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Node search */}
        <Box sx={{ position: 'relative' }}>
          <IconButton
            size="small"
            onClick={() => { setNodeSearchOpen((v) => !v); setNodeSearchQuery(''); }}
            title="Search nodes"
            sx={{
              color: nodeSearchOpen ? '#a78bfa' : 'text.secondary',
              bgcolor: nodeSearchOpen ? 'rgba(139,92,246,0.12)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(139,92,246,0.1)' }
            }}
          >
            <SearchIcon size={16} strokeWidth={2} />
          </IconButton>
          {nodeSearchOpen && (
            <Box sx={{
              position: 'absolute', top: '100%', right: 0, mt: 0.5, zIndex: 50,
              bgcolor: '#18181b', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.5)', width: 320, maxHeight: 400,
              display: 'flex', flexDirection: 'column'
            }}>
              <TextField
                inputRef={nodeSearchRef}
                autoFocus
                size="small"
                fullWidth
                placeholder="Search nodes..."
                value={nodeSearchQuery}
                onChange={(e) => setNodeSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { setNodeSearchOpen(false); setNodeSearchQuery(''); }
                }}
                sx={{ px: 1, pt: 1, '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
              />
              {nodeSearchQuery.length >= 3 && (
                <Box sx={{ overflow: 'auto', maxHeight: 320, px: 0.5, py: 0.5 }} className="vr-scroll-y">
                  {nodeSearchResults.length === 0 && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                      No matches
                    </Typography>
                  )}
                  {nodeSearchResults.map((n) => {
                    const d = n.data || {};
                    const preview = d.text || d.label || d.imageName || d.decisionName || d.characterName || n.type;
                    return (
                      <Box
                        key={n.id}
                        onClick={() => focusNode(n.id)}
                        sx={{
                          px: 1, py: 0.5, borderRadius: '4px', cursor: 'pointer',
                          '&:hover': { bgcolor: 'rgba(139,92,246,0.1)' },
                          display: 'flex', alignItems: 'center', gap: 0.75
                        }}
                      >
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#a78bfa', flexShrink: 0, minWidth: 56 }}>
                          {n.type.replace('sf', '')}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1, minWidth: 0 }}>
                          {typeof preview === 'string' ? preview.slice(0, 80) : String(preview).slice(0, 80)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Button
          size="small"
          variant="outlined"
          startIcon={<Bot size={16} strokeWidth={2} />}
          onClick={() => primarySelected && openNexusWithContext(primarySelected)}
          disabled={!primarySelected}
          title="Ask Nexus about this node"
          sx={{
            borderColor: primarySelected ? 'rgba(139,92,246,0.4)' : 'rgba(255,255,255,0.1)',
            color: primarySelected ? '#a78bfa' : 'text.disabled',
            '&:hover': { borderColor: 'rgba(139,92,246,0.6)', bgcolor: 'rgba(139,92,246,0.08)' },
            '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.06)', color: 'text.disabled' }
          }}
        >
          Ask Nexus
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Sparkles size={16} strokeWidth={2} />}
          onClick={() => setGeminiOpen(true)}
          title="Review sfDialogue translations with Gemini"
          sx={{
            borderColor: 'rgba(167, 139, 250, 0.4)',
            color: '#a78bfa',
            '&:hover': { borderColor: 'rgba(167, 139, 250, 0.6)', bgcolor: 'rgba(167, 139, 250, 0.08)' }
          }}
        >
          Gemini
        </Button>
        <Button size="small" variant="outlined" startIcon={<AlertTriangle size={16} strokeWidth={2} />} onClick={checkGraph} title="Find graph errors">
          Check
        </Button>
        <Button size="small" variant="outlined" startIcon={<Ruler size={16} strokeWidth={2} />} onClick={autoLayoutNodes} title="Auto-layout nodes">
          Layout
        </Button>
        <Button size="small" variant="outlined" startIcon={<Braces size={16} strokeWidth={2} />} onClick={() => { setJsonDraft(jsonPreview); setClearConfirm(false); setJsonOpen(true); }}>
          JSON
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={16} strokeWidth={2} />}
          onClick={() => persist()}
          sx={{ minWidth: 80 }}
        >
          Save
        </Button>
      </Box>

      {/* Palette + Canvas + Inspector */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Gemini translation review — left side panel (slots before palette so canvas remains interactive) */}
        <GeminiReviewPanel
          open={geminiOpen}
          onClose={() => setGeminiOpen(false)}
          buildGraphJson={buildGraphJsonForGemini}
          applyProposals={applyGeminiProposals}
          storyflowId={storyflowId}
          teamId={activeTeam?.id}
          getToken={getToken}
          onFocusNode={focusNode}
        />
        {/* Left palette panel — hidden while the Gemini review panel is open */}
        {!geminiOpen && (
        <Box
          className="wf-palette vr-scroll-y"
          sx={{ p: 0.75, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {characterPaletteItems.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mb: 0.5, mt: 0.5, px: 0.75, display: 'block', fontSize: '0.6875rem' }}>
                Characters
              </Typography>
              <Box display="flex" flexDirection="column">
                {characterPaletteItems.map((item) => (
                  <Box
                    key={`char-${item.defaultData.speakerId}`}
                    className="wf-palette-item"
                    draggable
                    onDragStart={(e) => addFromPaletteDragStart(e, item.type, item.defaultData)}
                    onClick={() => addFromPaletteClick(item.type, item.defaultData)}
                    sx={{ py: 0.55, px: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'grab' }}
                  >
                    {item.avatarUrl ? (
                      <Avatar src={item.avatarUrl} sx={{ width: 22, height: 22 }} />
                    ) : (
                      <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: item.iconWellBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <item.Icon size={13} strokeWidth={2} style={{ color: item.iconColor }} />
                      </Box>
                    )}
                    <Typography component="span" noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{item.title}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mt: characterPaletteItems.length > 0 ? 1 : 0.5, mb: 0.5, px: 0.75, display: 'block', fontSize: '0.6875rem' }}>
            Nodes
          </Typography>
          <Box display="flex" flexDirection="column">
            {SF_PALETTE_ITEMS.map((item) => {
              const Icon = item.Icon;
              return (
                <Box
                  key={item.type}
                  className="wf-palette-item"
                  draggable
                  onDragStart={(e) => addFromPaletteDragStart(e, item.type, item.defaultData)}
                  onClick={() => addFromPaletteClick(item.type, item.defaultData)}
                  sx={{ py: 0.55, px: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'grab' }}
                >
                  <Box sx={{ width: 22, height: 22, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: item.iconWellBg, flexShrink: 0 }}>
                    <Icon size={13} strokeWidth={2} color={item.iconColor} />
                  </Box>
                  <Typography component="span" noWrap sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{item.title}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        )}

        <Box ref={paneRef} sx={{ flex: 1, minWidth: 0, position: 'relative' }} onDragOver={onDragOver} onDrop={onDrop}>
          <SfCharactersProvider value={characters}>
          <SfLanguageProvider value={sfLang}>
          <SfActionsProvider value={sfActions}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            onReconnect={onReconnect}
            onReconnectStart={onReconnectStart}
            onReconnectEnd={onReconnectEnd}
            isValidConnection={isValidConnection}
            onMoveEnd={onMoveEnd}
            onPaneContextMenu={onPaneContextMenu}
            onNodeContextMenu={onNodeContextMenu}
            onSelectionContextMenu={onSelectionContextMenu}
            onNodeDragStart={startInteracting}
            onNodeDragStop={stopInteracting}
            onSelectionStart={startInteracting}
            onSelectionEnd={stopInteracting}
            selectionMode={SelectionMode.Full}
            nodeTypes={sfNodeTypes}
            edgeTypes={edgeTypes}
            connectionLineComponent={BleuConnectionLine}
            defaultEdgeOptions={{ type: 'bleu', animated: true }}
            defaultViewport={hasSavedViewport.current ? viewportRef.current : undefined}
            fitView={!hasSavedViewport.current}
            fitViewOptions={{ padding: 0.2, duration: 0 }}
            minZoom={0.05}
            maxZoom={2}
            autoPanOnNodeDrag={false}
            autoPanOnConnect={false}
            panOnDrag={[1]}
            selectionOnDrag
            proOptions={{ hideAttribution: true }}
            colorMode="dark"
            className="bleu-workflow"
            deleteKeyCode={['Backspace', 'Delete']}
          >
            <Background variant="dots" gap={20} size={1} color="rgba(255, 255, 255, 0.145)" />
            <Controls showInteractive={false} fitViewOptions={{ duration: 0 }}>
              <ControlButton
                type="button"
                className="wf-controls-map-toggle"
                onClick={() => setMiniMapVisible((v) => !v)}
                title={miniMapVisible ? 'Hide minimap' : 'Show minimap'}
                aria-label={miniMapVisible ? 'Hide minimap' : 'Show minimap'}
                aria-pressed={miniMapVisible}
              >
                <MapIcon size={12} strokeWidth={2} aria-hidden />
              </ControlButton>
            </Controls>
            {miniMapVisible ? (
              <MiniMap
                nodeStrokeWidth={2}
                zoomable
                pannable
                maskColor="rgba(0, 0, 0, 0.28)"
                maskStrokeColor="rgba(255, 255, 255, 0.12)"
                nodeColor="rgba(99, 102, 241, 0.42)"
                nodeStrokeColor="rgba(255, 255, 255, 0.12)"
                bgColor="rgba(24, 24, 27, 0.5)"
              />
            ) : null}
          </ReactFlow>
          </SfActionsProvider>
          </SfLanguageProvider>
          </SfCharactersProvider>
        </Box>

        {/* Inspector panel */}
        {primarySelected && !interacting && (
          <Box
            className="wf-inspector vr-scroll-y"
            sx={{
              width: 300, flexShrink: 0, p: 1.5, overflowY: 'auto', overflowX: 'hidden',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              bgcolor: '#18181b'
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              {{ sfDialogue: 'Dialogue', sfImage: 'Image', sfVideo: 'Video', sfNextChapter: 'Next Chapter', sfNextFlow: 'Next Chapter', sfDecision: 'Decision', sfBranch: 'Switch', sfStart: 'Start', sfRpSwitch: 'RP Switch', sfTimeskip: 'Timeskip', sfIntroduce: 'Introduce', sfStatus: 'Status', sfInsta: 'Insta Post' }[primarySelected.type] || primarySelected.type} Inspector
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>

              {/* ── Dialogue node inspector ── */}
              {primarySelected.type === 'sfDialogue' && (
                <>
                  {/* Audio for the dialogue line */}
                  <Box sx={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', p: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                      Voiceover
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {primarySelected.data?.audioUrl && (
                        <IconButton
                          size="small"
                          onClick={() => toggleInspectorAudio(primarySelected.data.audioUrl)}
                          sx={{ p: 0.5, color: inspectorAudioPlaying ? '#fbbf24' : MUTED, '&:hover': { color: '#fbbf24' } }}
                        >
                          {inspectorAudioPlaying ? <Square size={14} fill="#fbbf24" /> : <Play size={14} fill="currentColor" />}
                        </IconButton>
                      )}
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: primarySelected.data?.audio ? '#fafafa' : MUTED, flex: 1 }} noWrap>
                        {primarySelected.data?.audio || 'No audio'}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpen size={14} />}
                        onClick={() => setStorageBrowser({ fileFilter: 'audio', nodeField: 'audio', title: 'Select Audio' })}
                      >
                        Browse
                      </Button>
                      {primarySelected.data?.audio && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => { stopInspectorAudio(); updateSelectedData({ audio: '', audioUrl: '' }); }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Answer settings */}
                  {(() => { const _ac = Math.max((primarySelected.data?.variants || []).length, (primarySelected.data?.variants_en || []).length); return _ac > 0 ? (
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
                      Answer Settings
                    </Typography>
                  ) : null; })()}
                  {Array.from({ length: Math.max((primarySelected.data?.variants || []).length, (primarySelected.data?.variants_en || []).length) }, (_, i) => {
                    const rel = (primarySelected.data?.relationships || [])[i] ?? 0;
                    const tier = (primarySelected.data?.tiers || [])[i] ?? '';
                    const v = (primarySelected.data?.variants || [])[i] || '';
                    const displayVariant = ((sfLang === 'en' ? primarySelected.data?.variants_en : primarySelected.data?.variants) || [])[i] || v || (primarySelected.data?.variants_en || [])[i] || '';
                    return (
                      <Box key={i} sx={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', p: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa', mb: 0.75 }}>
                          {displayVariant || `Answer ${i + 1}`}
                        </Typography>
                        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', mb: 0.75 }} />
                        {/* Relationship slider */}
                        <Box sx={{ px: 0.5 }}>
                          <Typography variant="caption" sx={{
                            color: rel > 0 ? 'rgb(74, 222, 128)' : rel < 0 ? 'rgb(248, 113, 113)' : 'text.secondary',
                            fontWeight: 600
                          }}>
                            Relationship: {rel > 0 ? '+' : ''}{rel}
                          </Typography>
                          <Slider
                            size="small"
                            value={rel}
                            min={-50}
                            max={50}
                            onChange={(_, val) => {
                              const nr = [...(primarySelected.data?.relationships || [])];
                              while (nr.length <= i) nr.push(0);
                              nr[i] = val;
                              updateSelectedData({ relationships: nr });
                            }}
                            sx={{
                              color: rel > 0 ? 'rgb(74, 222, 128)' : rel < 0 ? 'rgb(248, 113, 113)' : 'rgba(161, 161, 170, 0.5)',
                              '& .MuiSlider-thumb': { width: 14, height: 14 },
                              '& .MuiSlider-track': { height: 4 },
                              '& .MuiSlider-rail': { height: 4, bgcolor: 'rgba(255,255,255,0.08)' }
                            }}
                          />
                        </Box>
                        {/* Premium Tier selector */}
                        <Box sx={{ mt: 0.5 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Premium Tier</InputLabel>
                            <Select
                              label="Premium Tier"
                              value={tier}
                              onChange={(e) => {
                                const nt = [...(primarySelected.data?.tiers || [])];
                                while (nt.length <= i) nt.push('');
                                nt[i] = e.target.value;
                                updateSelectedData({ tiers: nt });
                              }}
                            >
                              <MenuItem value="">Free</MenuItem>
                              {patreonTiers.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* ── Image node inspector ── */}
              {primarySelected.type === 'sfImage' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {primarySelected.data?.imagePreview512 && (
                      <Box
                        component="img"
                        src={primarySelected.data.imagePreview512}
                        sx={{ width: '100%', maxHeight: 160, borderRadius: '6px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)' }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: primarySelected.data?.imageName ? '#fafafa' : MUTED, flex: 1 }} noWrap>
                        {primarySelected.data?.imageName || 'No image selected'}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpen size={14} />}
                        onClick={() => setStorageBrowser({ fileFilter: 'image', nodeField: 'image' })}
                      >
                        Browse
                      </Button>
                      {primarySelected.data?.imageName && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => updateSelectedData({ imageName: '', imageUrl: '', imagePreview512: '' })}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box
                    onClick={() => updateSelectedData({ playerSends: !primarySelected.data?.playerSends })}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '6px', cursor: 'pointer',
                      border: '1px solid',
                      borderColor: primarySelected.data?.playerSends ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255,255,255,0.08)',
                      bgcolor: primarySelected.data?.playerSends ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                      '&:hover': { bgcolor: primarySelected.data?.playerSends ? 'rgba(99, 102, 241, 0.14)' : 'rgba(255,255,255,0.04)' }
                    }}
                  >
                    <Smartphone size={14} style={{ color: primarySelected.data?.playerSends ? '#a78bfa' : MUTED, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: primarySelected.data?.playerSends ? '#a78bfa' : '#fafafa' }}>
                      Player sends
                    </Typography>
                  </Box>
                </>
              )}

              {/* ── Next Chapter node inspector ── */}
              {(primarySelected.type === 'sfNextChapter' || primarySelected.type === 'sfNextFlow') && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Target chapter
                  </Typography>
                  {/* None option */}
                  <Box
                    onClick={() => updateSelectedData({ targetChapterName: '' })}
                    sx={{
                      p: 0.75, borderRadius: '6px', cursor: 'pointer',
                      border: '1px solid',
                      borderColor: !(primarySelected.data?.targetChapterName || primarySelected.data?.targetFlowName)
                        ? 'rgba(251, 191, 36, 0.5)' : 'rgba(255,255,255,0.08)',
                      bgcolor: !(primarySelected.data?.targetChapterName || primarySelected.data?.targetFlowName)
                        ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.06)' }
                    }}
                  >
                    <Typography sx={{ fontSize: '0.75rem', color: MUTED, fontStyle: 'italic' }}>None</Typography>
                  </Box>
                  {teamStoryflows.map((sf) => {
                    const active = (primarySelected.data?.targetChapterName || primarySelected.data?.targetFlowName) === sf.name;
                    const coverUrl = sf.cover?.url128 || sf.cover?.url64 || null;
                    return (
                      <Box
                        key={sf.id}
                        onClick={() => updateSelectedData({ targetChapterName: sf.name })}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '6px', cursor: 'pointer',
                          border: '1px solid',
                          borderColor: active ? 'rgba(251, 191, 36, 0.5)' : 'rgba(255,255,255,0.08)',
                          bgcolor: active ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.06)' }
                        }}
                      >
                        {coverUrl ? (
                          <Box component="img" src={coverUrl} sx={{ width: 40, height: 40, borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <Box sx={{ width: 40, height: 40, borderRadius: '4px', bgcolor: 'rgba(251, 191, 36, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(251, 191, 36, 0.6)' }}>{sf.number ?? '?'}</Typography>
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa' }} noWrap>
                            {sf.number != null ? `Ch. ${sf.number}` : 'Chapter'}
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: MUTED }} noWrap>{sf.name}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* ── Decision node inspector ── */}
              {primarySelected.type === 'sfDecision' && (() => {
                const opts = primarySelected.data?.options || (primarySelected.data?.optionA ? [primarySelected.data.optionA, primarySelected.data.optionB || 'Option B'] : ['Option A', 'Option B']);
                return (
                  <>
                    <TextField
                      label="Decision name"
                      fullWidth
                      size="small"
                      value={primarySelected.data?.decisionName ?? ''}
                      onChange={(e) => updateSelectedData({ decisionName: e.target.value })}
                    />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Options
                    </Typography>
                    {opts.map((opt, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={opt}
                          placeholder={`Option ${i + 1}`}
                          onChange={(e) => {
                            const next = [...opts];
                            next[i] = e.target.value;
                            updateSelectedData({ options: next });
                          }}
                        />
                        {opts.length > 2 && (
                          <IconButton
                            size="small"
                            onClick={() => updateSelectedData({ options: opts.filter((_, j) => j !== i) })}
                            sx={{ color: 'rgba(248,113,113,0.7)', '&:hover': { color: '#f87171' } }}
                          >
                            <X size={14} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Plus size={14} />}
                      onClick={() => updateSelectedData({ options: [...opts, `Option ${opts.length + 1}`] })}
                      sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                    >
                      Add Option
                    </Button>
                  </>
                );
              })()}

              {/* ── Switch node inspector ── */}
              {primarySelected.type === 'sfBranch' && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Decision to switch on
                  </Typography>
                  {switchDecisions.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {switchDecisions.map((d, i) => (
                        <Box
                          key={d.nodeId || i}
                          onClick={() => updateSelectedData({
                            decisionRef: d.decisionName,
                            switchOptions: d.options
                          })}
                          sx={{
                            p: 0.75, borderRadius: '6px', cursor: 'pointer',
                            border: '1px solid',
                            borderColor: primarySelected.data?.decisionRef === d.decisionName
                              ? 'rgba(56, 189, 248, 0.5)'
                              : 'rgba(255,255,255,0.08)',
                            bgcolor: primarySelected.data?.decisionRef === d.decisionName
                              ? 'rgba(56, 189, 248, 0.08)'
                              : 'transparent',
                            '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.06)' }
                          }}
                        >
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa' }}>
                            {d.chapterNumber !== undefined && <span style={{ color: MUTED, marginRight: 4 }}>Ch.{d.chapterNumber}</span>}
                            {d.decisionName}
                          </Typography>
                          {d.options.map((o, j) => (
                            <Typography key={j} sx={{ fontSize: '0.6rem', color: MUTED, pl: 1 }}>
                              • {o}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No decisions available. Add a Decision node or load previous chapter decisions via Start node.
                    </Typography>
                  )}
                </>
              )}

              {/* ── Video node inspector ── */}
              {primarySelected.type === 'sfVideo' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {primarySelected.data?.videoPreview512 && (
                      <VideoHoverPreview
                        thumbnailSrc={primarySelected.data.videoPreview512}
                        videoSrc={primarySelected.data.videoUrl}
                        sx={{ width: '100%', height: 160, borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: primarySelected.data?.videoName ? '#fafafa' : MUTED, flex: 1 }} noWrap>
                        {primarySelected.data?.videoName || 'No video selected'}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpen size={14} />}
                        onClick={() => setStorageBrowser({ fileFilter: 'video', nodeField: 'video' })}
                      >
                        Browse
                      </Button>
                      {primarySelected.data?.videoName && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => updateSelectedData({ videoName: '', videoUrl: '', videoPreview512: '' })}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', p: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                      Voiceover
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {primarySelected.data?.audioUrl && (
                        <IconButton
                          size="small"
                          onClick={() => toggleInspectorAudio(primarySelected.data.audioUrl)}
                          sx={{ p: 0.5, color: inspectorAudioPlaying ? '#fbbf24' : MUTED, '&:hover': { color: '#fbbf24' } }}
                        >
                          {inspectorAudioPlaying ? <Square size={14} fill="#fbbf24" /> : <Play size={14} fill="currentColor" />}
                        </IconButton>
                      )}
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: primarySelected.data?.audio ? '#fafafa' : MUTED, flex: 1 }} noWrap>
                        {primarySelected.data?.audio || 'No audio'}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderOpen size={14} />}
                        onClick={() => setStorageBrowser({ fileFilter: 'audio', nodeField: 'audio', title: 'Select Audio' })}
                      >
                        Browse
                      </Button>
                      {primarySelected.data?.audio && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => { stopInspectorAudio(); updateSelectedData({ audio: '', audioUrl: '' }); }}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {/* Answer settings (same as Dialogue) */}
                  {(() => { const _ac = Math.max((primarySelected.data?.variants || []).length, (primarySelected.data?.variants_en || []).length); return _ac > 0 ? (
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
                      Answer Settings
                    </Typography>
                  ) : null; })()}
                  {Array.from({ length: Math.max((primarySelected.data?.variants || []).length, (primarySelected.data?.variants_en || []).length) }, (_, i) => {
                    const rel = (primarySelected.data?.relationships || [])[i] ?? 0;
                    const tier = (primarySelected.data?.tiers || [])[i] ?? '';
                    const v = (primarySelected.data?.variants || [])[i] || '';
                    const displayVariant = ((sfLang === 'en' ? primarySelected.data?.variants_en : primarySelected.data?.variants) || [])[i] || v || (primarySelected.data?.variants_en || [])[i] || '';
                    return (
                      <Box key={i} sx={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', p: 1 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa', mb: 0.75 }}>
                          {displayVariant || `Answer ${i + 1}`}
                        </Typography>
                        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', mb: 0.75 }} />
                        <Box sx={{ px: 0.5 }}>
                          <Typography variant="caption" sx={{
                            color: rel > 0 ? 'rgb(74, 222, 128)' : rel < 0 ? 'rgb(248, 113, 113)' : 'text.secondary',
                            fontWeight: 600
                          }}>
                            Relationship: {rel > 0 ? '+' : ''}{rel}
                          </Typography>
                          <Slider
                            size="small"
                            value={rel}
                            min={-50}
                            max={50}
                            onChange={(_, val) => {
                              const nr = [...(primarySelected.data?.relationships || [])];
                              while (nr.length <= i) nr.push(0);
                              nr[i] = val;
                              updateSelectedData({ relationships: nr });
                            }}
                            sx={{
                              color: rel > 0 ? 'rgb(74, 222, 128)' : rel < 0 ? 'rgb(248, 113, 113)' : 'rgba(161, 161, 170, 0.5)',
                              '& .MuiSlider-thumb': { width: 14, height: 14 },
                              '& .MuiSlider-track': { height: 4 },
                              '& .MuiSlider-rail': { height: 4, bgcolor: 'rgba(255,255,255,0.08)' }
                            }}
                          />
                        </Box>
                        <Box sx={{ mt: 0.5 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Premium Tier</InputLabel>
                            <Select
                              label="Premium Tier"
                              value={tier}
                              onChange={(e) => {
                                const nt = [...(primarySelected.data?.tiers || [])];
                                while (nt.length <= i) nt.push('');
                                nt[i] = e.target.value;
                                updateSelectedData({ tiers: nt });
                              }}
                            >
                              <MenuItem value="">Free</MenuItem>
                              {patreonTiers.map((t) => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    );
                  })}
                </>
              )}

              {/* ── RP Switch node inspector ── */}
              {primarySelected.type === 'sfRpSwitch' && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Character
                  </Typography>
                  {characters.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {characters.map((c) => {
                        const active = primarySelected.data?.characterId === c.id;
                        return (
                          <Box
                            key={c.id}
                            onClick={() => updateSelectedData({ characterId: c.id, characterName: c.name })}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '6px', cursor: 'pointer',
                              border: '1px solid',
                              borderColor: active ? 'rgba(251, 146, 60, 0.5)' : 'rgba(255,255,255,0.08)',
                              bgcolor: active ? 'rgba(251, 146, 60, 0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(251, 146, 60, 0.06)' }
                            }}
                          >
                            <Avatar src={c.avatar?.url64 || undefined} sx={{ width: 24, height: 24, fontSize: '0.6rem' }}>
                              {c.name?.[0]}
                            </Avatar>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa' }}>{c.name}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No characters available.
                    </Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      Relationship Level: {primarySelected.data?.relationshipLevel ?? 0}
                    </Typography>
                    <Slider
                      size="small"
                      value={primarySelected.data?.relationshipLevel ?? 0}
                      min={-100}
                      max={100}
                      onChange={(_, val) => updateSelectedData({ relationshipLevel: val })}
                      sx={{
                        color: 'rgb(251, 146, 60)',
                        '& .MuiSlider-thumb': { width: 14, height: 14 },
                        '& .MuiSlider-track': { height: 4 },
                        '& .MuiSlider-rail': { height: 4, bgcolor: 'rgba(255,255,255,0.08)' }
                      }}
                    />
                  </Box>
                </>
              )}

              {/* ── Timeskip node inspector ── */}
              {primarySelected.type === 'sfTimeskip' && (
                <>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {['set', 'skip'].map((m) => (
                      <Box
                        key={m}
                        onClick={() => updateSelectedData({ timeskipMode: m })}
                        sx={{
                          flex: 1, textAlign: 'center', py: 0.5, borderRadius: '6px', cursor: 'pointer',
                          fontSize: '0.75rem', fontWeight: 600,
                          color: (primarySelected.data?.timeskipMode || 'set') === m ? '#c084fc' : MUTED,
                          bgcolor: (primarySelected.data?.timeskipMode || 'set') === m ? 'rgba(192, 132, 252, 0.12)' : 'rgba(255,255,255,0.04)',
                          border: '1px solid',
                          borderColor: (primarySelected.data?.timeskipMode || 'set') === m ? 'rgba(192, 132, 252, 0.3)' : 'rgba(255,255,255,0.08)',
                        }}
                      >
                        {m === 'set' ? 'Set Time' : 'Skip'}
                      </Box>
                    ))}
                  </Box>
                  <TextField
                    label="Hours"
                    fullWidth
                    size="small"
                    type="number"
                    value={primarySelected.data?.hours ?? 0}
                    onChange={(e) => updateSelectedData({ hours: Math.max(0, parseInt(e.target.value) || 0) })}
                    inputProps={{ min: 0, max: 23 }}
                  />
                  <TextField
                    label="Minutes"
                    fullWidth
                    size="small"
                    type="number"
                    value={primarySelected.data?.minutes ?? 0}
                    onChange={(e) => updateSelectedData({ minutes: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)) })}
                    inputProps={{ min: 0, max: 59 }}
                  />
                </>
              )}

              {/* ── Introduce node inspector ── */}
              {primarySelected.type === 'sfIntroduce' && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Character to introduce
                  </Typography>
                  {characters.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {characters.map((c) => {
                        const active = primarySelected.data?.characterId === c.id;
                        return (
                          <Box
                            key={c.id}
                            onClick={() => updateSelectedData({ characterId: c.id, characterName: c.name })}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '6px', cursor: 'pointer',
                              border: '1px solid',
                              borderColor: active ? 'rgba(52, 211, 153, 0.5)' : 'rgba(255,255,255,0.08)',
                              bgcolor: active ? 'rgba(52, 211, 153, 0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(52, 211, 153, 0.06)' }
                            }}
                          >
                            <Avatar src={c.avatar?.url64 || undefined} sx={{ width: 24, height: 24, fontSize: '0.6rem' }}>
                              {c.name?.[0]}
                            </Avatar>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa' }}>{c.name}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No characters available.
                    </Typography>
                  )}
                </>
              )}

              {/* ── Status node inspector ── */}
              {primarySelected.type === 'sfStatus' && (() => {
                const emojiBtnRef = React.createRef();
                return (
                  <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Character
                    </Typography>
                    {characters.length > 0 ? (
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {characters.map((c) => {
                          const active = primarySelected.data?.characterId === c.id;
                          return (
                            <Box
                              key={c.id}
                              onClick={() => updateSelectedData({ characterId: c.id, characterName: c.name })}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '6px', cursor: 'pointer',
                                border: '1px solid',
                                borderColor: active ? 'rgba(251, 191, 36, 0.5)' : 'rgba(255,255,255,0.08)',
                                bgcolor: active ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.06)' }
                              }}
                            >
                              <Avatar src={c.avatar?.url64 || undefined} sx={{ width: 24, height: 24, fontSize: '0.6rem' }}>
                                {c.name?.[0]}
                              </Avatar>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa' }}>{c.name}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No characters available.
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
                      Status Emoji
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: 1,
                        p: 1, borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        bgcolor: 'rgba(255,255,255,0.04)',
                        minHeight: 36
                      }}>
                        {primarySelected.data?.statusEmoji ? (
                          <Box sx={{ fontSize: '1.5rem', lineHeight: 1 }}>{primarySelected.data.statusEmoji}</Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.75rem', color: MUTED, fontStyle: 'italic' }}>No emoji</Typography>
                        )}
                      </Box>
                      <Button
                        ref={emojiBtnRef}
                        size="small"
                        variant="outlined"
                        startIcon={<Smile size={14} />}
                        onClick={(e) => {
                          const anchor = emojiBtnRef.current || e.currentTarget;
                          if (window.__sfStatusEmojiOpen) {
                            window.__sfStatusEmojiOpen = false;
                            window.__sfStatusEmojiForceUpdate?.();
                          } else {
                            window.__sfStatusEmojiAnchor = anchor;
                            window.__sfStatusEmojiOpen = true;
                            window.__sfStatusEmojiForceUpdate?.();
                          }
                        }}
                      >
                        Pick
                      </Button>
                      {primarySelected.data?.statusEmoji && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => updateSelectedData({ statusEmoji: '' })}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                    <StatusEmojiPickerWrapper
                      onSelect={(emoji) => updateSelectedData({ statusEmoji: emoji })}
                    />
                  </>
                );
              })()}

              {/* ── Insta Post node inspector ── */}
              {primarySelected.type === 'sfInsta' && (
                <>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Character
                  </Typography>
                  {characters.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {characters.map((c) => {
                        const active = primarySelected.data?.characterId === c.id;
                        return (
                          <Box
                            key={c.id}
                            onClick={() => updateSelectedData({ characterId: c.id, characterName: c.name })}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '6px', cursor: 'pointer',
                              border: '1px solid',
                              borderColor: active ? 'rgba(217, 70, 239, 0.5)' : 'rgba(255,255,255,0.08)',
                              bgcolor: active ? 'rgba(217, 70, 239, 0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(217, 70, 239, 0.06)' }
                            }}
                          >
                            <Avatar src={c.avatar?.url64 || undefined} sx={{ width: 24, height: 24, fontSize: '0.6rem' }}>
                              {c.name?.[0]}
                            </Avatar>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#fafafa' }}>{c.name}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No characters available.
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
                    Image
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {primarySelected.data?.imagePreview512 && (
                      <Box
                        component="img"
                        src={primarySelected.data.imagePreview512}
                        sx={{ width: '100%', maxHeight: 160, borderRadius: '6px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)' }}
                      />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: primarySelected.data?.imageName ? '#fafafa' : MUTED, flex: 1 }} noWrap>
                        {primarySelected.data?.imageName || 'No image selected'}
                      </Typography>
                      <Button size="small" variant="outlined" startIcon={<FolderOpen size={14} />} onClick={() => setStorageBrowser({ fileFilter: 'image', nodeField: 'image' })}>
                        Browse
                      </Button>
                      {primarySelected.data?.imageName && (
                        <Button size="small" color="error" onClick={() => updateSelectedData({ imageName: '', imageUrl: '', imagePreview512: '' })}>
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 1 }}>
                    Type
                  </Typography>
                  <Box display="flex" gap={0.5}>
                    {['public', 'private'].map((s) => (
                      <Box
                        key={s}
                        onClick={() => updateSelectedData({ instaSection: s })}
                        sx={{
                          px: 1.5, py: 0.5, borderRadius: '6px', cursor: 'pointer', textTransform: 'capitalize',
                          fontSize: '0.75rem', fontWeight: 600,
                          border: '1px solid',
                          borderColor: primarySelected.data?.instaSection === s ? 'rgba(217, 70, 239, 0.5)' : 'rgba(255,255,255,0.08)',
                          bgcolor: primarySelected.data?.instaSection === s ? 'rgba(217, 70, 239, 0.08)' : 'transparent',
                          color: primarySelected.data?.instaSection === s ? '#d946ef' : '#fafafa',
                          '&:hover': { bgcolor: 'rgba(217, 70, 239, 0.06)' }
                        }}
                      >
                        {s}
                      </Box>
                    ))}
                  </Box>

                  <TextField
                    label="Caption"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={primarySelected.data?.caption ?? ''}
                    onChange={(e) => updateSelectedData({ caption: e.target.value })}
                    sx={{ mt: 1 }}
                  />
                  <TextField
                    label="Likes"
                    type="number"
                    size="small"
                    fullWidth
                    value={primarySelected.data?.likes ?? 0}
                    onChange={(e) => updateSelectedData({ likes: Math.max(0, parseInt(e.target.value) || 0) })}
                  />
                </>
              )}

              {/* ── Start node inspector ── */}
              {primarySelected.type === 'sfStart' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={aiLoading === 'gemini' ? <CircularProgress size={14} /> : <Sparkles size={14} />}
                      disabled={!!aiLoading}
                      onClick={() => loadAiSummary('gemini')}
                      sx={{ fontSize: '0.75rem', justifyContent: 'flex-start' }}
                    >
                      {aiLoading === 'gemini' ? 'Generating...' : 'AI Summary Gemini'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={aiLoading === 'grok' ? <CircularProgress size={14} /> : <Sparkles size={14} />}
                      disabled={!!aiLoading}
                      onClick={() => loadAiSummary('grok')}
                      sx={{ fontSize: '0.75rem', justifyContent: 'flex-start' }}
                    >
                      {aiLoading === 'grok' ? 'Generating...' : 'AI Summary Grok'}
                    </Button>
                  </Box>
                  {primarySelected.data?.previousSummary && (
                    <Box sx={{ p: 1, borderRadius: '6px', bgcolor: 'rgba(250,250,250,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Previous Chapter Summary
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'rgba(250,250,250,0.7)' }}>
                        {sfLang === 'en' ? (primarySelected.data.previousSummary_en || primarySelected.data.previousSummary) : primarySelected.data.previousSummary}
                      </Typography>
                    </Box>
                  )}
                  {prevChapterDecisions.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Past Decisions ({prevChapterDecisions.length})
                      </Typography>
                      {prevChapterDecisions.map((d, i) => {
                        const opts = d.options || [];
                        const isTF = opts.length === 2 && opts[0] === 'True' && opts[1] === 'False';
                        return (
                          <Box key={d.nodeId || i} sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'rgba(250,250,250,0.7)' }}>
                                Ch.{d.chapterNumber} {d.decisionName}
                              </Typography>
                              {!isTF && opts.map((o, j) => (
                                <Typography key={j} sx={{ fontSize: '0.65rem', color: 'rgba(250,250,250,0.5)', pl: 1.5 }}>
                                  • {o}
                                </Typography>
                              ))}
                            </Box>
                            <Chip
                              label={isTF ? 'T/F' : 'Multiple'}
                              size="small"
                              sx={{ height: 18, fontSize: '0.55rem', fontWeight: 600, bgcolor: 'rgba(244,114,182,0.1)', color: 'rgba(244,114,182,0.8)', border: '1px solid rgba(244,114,182,0.15)' }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Context menu */}
      <Menu
        open={!!ctxMenu}
        onClose={closeCtx}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.mouseY, left: ctxMenu.mouseX } : undefined}
        transitionDuration={0}
        TransitionProps={{ timeout: 0 }}
        MenuListProps={{ dense: true, sx: { py: 0.5, px: 0.5 } }}
        PaperProps={{
          elevation: 0,
          sx: {
            ...CTX_MENU_PAPER_SX,
            bgcolor: '#18181b',
            minWidth: 220
          }
        }}
      >
        {/* Paste — when clipboard has content and not connecting from a pin */}
        {!ctxMenu?.connectFrom && (() => {
          let hasClip = false;
          try { hasClip = !!sessionStorage.getItem('flowcore_sf_clipboard'); } catch (_) { /* ignore */ }
          if (!hasClip) return null;
          return (
            <>
              <MenuItem
                onClick={() => {
                  pasteFromClipboard(ctxMenu?.flowPos);
                  closeCtx();
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '6px' }}
              >
                <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: 'rgba(74,222,128,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ClipboardPaste size={13} style={{ color: '#4ade80' }} />
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>Paste</Typography>
              </MenuItem>
              <Box component="hr" sx={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', my: 0.5 }} />
            </>
          );
        })()}

        {/* AI continuation — only when dragging from a pin */}
        {ctxMenu?.connectFrom && (
          <>
            <MenuItem
              onClick={() => {
                setAiContinueModal({
                  sourceNodeId: ctxMenu.connectFrom.nodeId,
                  sourceHandle: ctxMenu.connectFrom.handleId || 'flow-out',
                  flowPos: ctxMenu.flowPos
                });
                setAiContinuePrompt('');
                setAiContinueResult(null);
                closeCtx();
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, borderRadius: '6px' }}
            >
              <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: 'rgba(167,139,250,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Sparkles size={13} style={{ color: '#a78bfa' }} />
              </Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#a78bfa' }}>Continue with AI</Typography>
            </MenuItem>
            <Box component="hr" sx={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', my: 0.5 }} />
          </>
        )}

        {/* Characters as Dialogue nodes */}
        {characterPaletteItems.map((item) => (
          <MenuItem
            key={`char-${item.defaultData.speakerId}`}
            onClick={() => addNodeFromCtx(item.type, item.defaultData)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '6px' }}
          >
            {item.avatarUrl ? (
              <Avatar src={item.avatarUrl} sx={{ width: 22, height: 22 }} />
            ) : (
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: item.iconWellBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <item.Icon size={13} style={{ color: item.iconColor }} />
              </Box>
            )}
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{item.title}</Typography>
          </MenuItem>
        ))}

        {characterPaletteItems.length > 0 && <Box component="hr" sx={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', my: 0.5 }} />}

        {SF_PALETTE_ITEMS.map((item) => (
          <MenuItem
            key={item.type}
            onClick={() => addNodeFromCtx(item.type, item.defaultData)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '6px' }}
          >
            <Box sx={{ width: 22, height: 22, borderRadius: '6px', bgcolor: item.iconWellBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <item.Icon size={13} style={{ color: item.iconColor }} />
            </Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{item.title}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Node context menu (right-click on a node) */}
      <Menu
        open={!!nodeCtxMenu}
        onClose={closeCtx}
        anchorReference="anchorPosition"
        anchorPosition={nodeCtxMenu ? { top: nodeCtxMenu.mouseY, left: nodeCtxMenu.mouseX } : undefined}
        transitionDuration={0}
        TransitionProps={{ timeout: 0 }}
        MenuListProps={{ dense: true, sx: { py: 0.5, px: 0.5 } }}
        PaperProps={{
          elevation: 0,
          sx: {
            ...CTX_MENU_PAPER_SX,
            bgcolor: '#18181b',
            minWidth: 180
          }
        }}
      >
        <MenuItem
          onClick={() => nodeCtxMenu && playFromHere(nodeCtxMenu.nodeId)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '4px', fontSize: '0.8125rem' }}
        >
          <Play size={16} strokeWidth={2} style={{ color: '#4ade80', flexShrink: 0 }} />
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: '0.8125rem' }}>
            Play From Here
          </Typography>
        </MenuItem>
        <Box component="hr" sx={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.1)', my: 0.5 }} />
        <MenuItem
          onClick={() => {
            if (nodeCtxMenu) copySelection(nodeCtxMenu.nodeId);
            closeCtx();
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '4px', fontSize: '0.8125rem' }}
        >
          <Copy size={16} strokeWidth={2} style={{ color: 'rgba(161,161,170,0.95)', flexShrink: 0 }} />
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: '0.8125rem' }}>
            Copy
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (nodeCtxMenu) openNexusWithContext(nodeCtxMenu.nodeId);
            closeCtx();
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '4px', fontSize: '0.8125rem' }}
        >
          <Bot size={16} strokeWidth={2} style={{ color: '#a78bfa', flexShrink: 0 }} />
          <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: '0.8125rem' }}>
            Ask Nexus
          </Typography>
        </MenuItem>
        {(() => {
          return (
            <MenuItem
              onClick={() => {
                if (nodeCtxMenu) {
                  const nid = nodeCtxMenu.nodeId;
                  setNodes((nds) => nds.filter((n) => n.id !== nid));
                  setEdges((eds) => eds.filter((e) => e.source !== nid && e.target !== nid));
                }
                closeCtx();
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1, borderRadius: '4px', fontSize: '0.8125rem' }}
            >
              <Trash2 size={16} strokeWidth={2} style={{ color: '#fca5a5', flexShrink: 0 }} />
              <Typography component="span" sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: '0.8125rem' }}>
                Delete
              </Typography>
            </MenuItem>
          );
        })()}
      </Menu>

      {/* Nexus chat drawer */}
      <NexusChatDrawer
        open={nexusDrawerOpen}
        onClose={() => setNexusDrawerOpen(false)}
        getToken={getToken}
        context={nexusContext}
      />

      {/* JSON dialog */}
      <Dialog
        open={jsonOpen}
        onClose={() => setJsonOpen(false)}
        maxWidth="md"
        fullWidth
        className="wf-json-dialog"
        PaperProps={{
          sx: {
            bgcolor: '#18181b',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>Graph JSON (logic + meta)</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            value={jsonDraft}
            onChange={(e) => setJsonDraft(e.target.value)}
            multiline
            fullWidth
            minRows={16}
            InputProps={{
              sx: {
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: 12,
                lineHeight: 1.5,
                color: '#e4e4e7',
                bgcolor: 'rgba(0,0,0,0.35)',
                alignItems: 'flex-start',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                '& textarea': {
                  resize: 'vertical',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent'
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              color="error"
              startIcon={<Trash2 size={14} strokeWidth={2} />}
              onClick={() => {
                if (!clearConfirm) {
                  setClearConfirm(true);
                  return;
                }
                const startNode = nodes.find((n) => n.type === 'sfStart');
                const kept = startNode
                  ? [{ ...startNode, data: { ...startNode.data } }]
                  : [{ id: newNodeId(), type: 'sfStart', position: { x: 400, y: 50 }, data: { previousSummary: '', accumulatedDecisions: [] } }];
                setNodes(kept);
                setEdges([]);
                setClearConfirm(false);
                setJsonOpen(false);
              }}
              sx={{ minWidth: 0 }}
            >
              {clearConfirm ? 'Confirm clear' : 'Clear graph'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<Copy size={16} strokeWidth={2} />}
              onClick={() => navigator.clipboard.writeText(jsonDraft)}
            >
              Copy
            </Button>
            {jsonDraft !== jsonPreview && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  try {
                    const parsed = JSON.parse(jsonDraft);
                    const g = parsed.graph || parsed;
                    const gm = parsed.graphMeta || { positions: {}, viewport: null };
                    const merged = mergeGraphWithMeta(g, gm);
                    setNodes(merged.nodes);
                    setEdges(merged.edges);
                    if (merged.viewport) {
                      viewportRef.current = merged.viewport;
                      hasSavedViewport.current = true;
                    }
                    setJsonOpen(false);
                  } catch (err) {
                    alert('Invalid JSON: ' + err.message);
                  }
                }}
              >
                Apply
              </Button>
            )}
            <Button onClick={() => setJsonOpen(false)}>Close</Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Play From Here — fullscreen modal */}
      <Dialog
        open={!!playModal}
        onClose={() => setPlayModal(null)}
        fullScreen
        PaperProps={{
          sx: { bgcolor: '#000', backgroundImage: 'none', display: 'flex', flexDirection: 'column' }
        }}
      >
        {/* Toolbar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
          borderBottom: '1px solid rgba(255,255,255,0.08)', bgcolor: '#18181b', flexShrink: 0
        }}>
          <Box sx={{ height: 20, width: '1px', bgcolor: 'rgba(255,255,255,0.1)', mx: 0.5 }} />
          {SCREEN_PRESETS.map((preset) => {
            const active = preset.id === playPresetId;
            return (
              <Box
                key={preset.id}
                onClick={() => setPlayPresetId(preset.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  borderRadius: '6px', px: 1, py: 0.5, cursor: 'pointer',
                  fontSize: '0.75rem', fontWeight: 500,
                  color: active ? '#a78bfa' : 'rgba(161,161,170,0.7)',
                  bgcolor: active ? 'rgba(139,92,246,0.12)' : 'transparent',
                  border: active ? '1px solid rgba(139,92,246,0.3)' : '1px solid transparent',
                  '&:hover': { color: active ? '#a78bfa' : '#d4d4d8', bgcolor: active ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.04)' }
                }}
              >
                <Smartphone size={13} strokeWidth={2} />
                {preset.label}
              </Box>
            );
          })}
          <Box sx={{ flex: 1 }} />
          {/* Tier selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {[{ id: '', label: 'Free' }, ...patreonTiers.map((t) => ({ id: t, label: t }))].map((tier) => (
              <Box
                key={tier.id}
                component="button"
                onClick={() => { setPlayTestTier(tier.id); setPlayIframeKey((k) => k + 1); }}
                sx={{
                  px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', border: 'none', transition: 'all 0.15s',
                  bgcolor: tier.id === playTestTier ? 'rgba(251,191,36,0.15)' : 'transparent',
                  color: tier.id === playTestTier ? '#fbbf24' : 'rgba(161,161,170,0.7)',
                  outline: tier.id === playTestTier ? '1px solid rgba(251,191,36,0.3)' : 'none',
                  '&:hover': { color: '#d4d4d8', bgcolor: 'rgba(255,255,255,0.05)' }
                }}
              >
                {tier.label}
              </Box>
            ))}
          </Box>
          <IconButton size="small" onClick={() => setPlayIframeKey((k) => k + 1)} sx={{ color: 'rgba(161,161,170,0.7)', '&:hover': { color: '#d4d4d8' } }} title="Reload">
            <RotateCcw size={16} strokeWidth={2} />
          </IconButton>
          <IconButton size="small" onClick={() => setPlayModal(null)} sx={{ color: 'rgba(161,161,170,0.7)', '&:hover': { color: '#fca5a5' } }} title="Close">
            <X size={18} strokeWidth={2} />
          </IconButton>
        </Box>
        {/* Game viewport */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', minHeight: 0 }}>
          {playModal && (
            <Box
              sx={{
                position: 'relative', bgcolor: '#000', overflow: 'hidden',
                aspectRatio: `${playPreset.w} / ${playPreset.h}`,
                height: '100%', maxWidth: '100%'
              }}
            >
              <iframe
                key={playIframeKey}
                src={playModal.src}
                title="Game"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              />
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Storage browser modal for file selection */}
      {/* AI suggestion preview modal */}
      <AdminModal
        open={suggestionModalOpen}
        onClose={() => { setSuggestionModalOpen(false); setSuggestionPreview(null); setSuggestionNodeId(null); }}
        title="AI Suggestion"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={() => { setSuggestionModalOpen(false); setSuggestionPreview(null); setSuggestionNodeId(null); }}>Cancel</Button>
            <Button variant="contained" onClick={applySuggestion}>Apply</Button>
          </>
        }
      >
        {suggestionPreview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.light', mb: 0.5, display: 'block' }}>
                Character dialogue
              </Typography>
              <Box sx={{ bgcolor: 'rgba(139,92,246,0.08)', borderRadius: 1, p: 1.5 }}>
                {(suggestionPreview.text || '').split('\n').filter(Boolean).map((line, i) => (
                  <Typography key={i} sx={{ fontSize: '0.85rem', color: 'text.primary', mb: 0.5 }}>{line}</Typography>
                ))}
              </Box>
            </Box>
            {suggestionPreview.variants?.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.light', mb: 0.5, display: 'block' }}>
                  MC reply options
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {suggestionPreview.variants.map((v, i) => (
                    <Box key={i} sx={{ bgcolor: 'rgba(99,102,241,0.08)', borderRadius: 1, px: 1.5, py: 0.75 }}>
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.primary' }}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </AdminModal>

      {/* AI continuation modal */}
      <AdminModal
        open={!!aiContinueModal}
        onClose={() => { if (!aiContinueLoading) { setAiContinueModal(null); setAiContinueResult(null); setAiContinuePrompt(''); } }}
        title="Continue with AI"
        maxWidth="sm"
        disableClose={aiContinueLoading}
        actions={
          aiContinueResult ? (
            <>
              <Button onClick={() => { setAiContinueResult(null); setAiContinuePrompt(''); }}>Back</Button>
              <Button variant="contained" onClick={applyAiContinuation} startIcon={<Plus size={16} />}>
                Apply ({aiContinueResult.nodes?.length || 0} nodes)
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setAiContinueModal(null); setAiContinuePrompt(''); }} disabled={aiContinueLoading}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleAiContinue}
                disabled={aiContinueLoading || !aiContinuePrompt.trim()}
                startIcon={aiContinueLoading ? <CircularProgress size={16} /> : <Sparkles size={16} />}
              >
                {aiContinueLoading ? 'Generating...' : 'Generate'}
              </Button>
            </>
          )
        }
      >
        {!aiContinueResult ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              Describe what the AI should suggest. Nexus will pick up the context of the current chapter, find relevant data in the knowledge base, and propose a continuation as nodes.
            </Typography>
            <TextField
              value={aiContinuePrompt}
              onChange={(e) => setAiContinuePrompt(e.target.value)}
              placeholder="Finish the chapter dramatically"
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              autoFocus
              disabled={aiContinueLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && aiContinuePrompt.trim()) {
                  e.preventDefault();
                  handleAiContinue();
                }
              }}
              InputProps={{
                sx: {
                  fontSize: '0.9rem',
                  color: '#e4e4e7',
                  bgcolor: 'rgba(0,0,0,0.25)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' }
                }
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {aiContinueResult.description && (
              <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#c4b5fd', fontStyle: 'italic' }}>
                  {aiContinueResult.description}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Suggested nodes ({aiContinueResult.nodes?.length || 0})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {(aiContinueResult.nodes || []).map((node, i) => {
                const typeLabels = {
                  sfDialogue: 'Dialogue', sfImage: 'Image', sfVideo: 'Video',
                  sfTimeskip: 'Timeskip', sfIntroduce: 'Introduce', sfStatus: 'Status',
                  sfDecision: 'Decision', sfRpSwitch: 'RP Switch', sfNextChapter: 'Next Chapter'
                };
                const typeColors = {
                  sfDialogue: '#a78bfa', sfImage: '#f87171', sfVideo: '#60a5fa',
                  sfTimeskip: '#c084fc', sfIntroduce: '#34d399', sfStatus: '#fbbf24',
                  sfDecision: '#f472b6', sfRpSwitch: '#fb923c', sfNextChapter: '#fbbf24'
                };
                const color = typeColors[node.type] || '#a1a1aa';
                const label = typeLabels[node.type] || node.type;

                let preview = '';
                if (node.type === 'sfDialogue' || node.type === 'sfVideo') {
                  const charName = characters.find((c) => c.characterId === node.speakerId)?.name || node.characterName || node.speakerId || '?';
                  const textPreview = (node.text || '').split('\n').filter(Boolean).slice(0, 2).join(' / ');
                  preview = `${charName}: ${textPreview}`;
                  if (node.variants?.length) preview += ` → [${node.variants.join(', ')}]`;
                } else if (node.type === 'sfImage') {
                  preview = node.imageName || '(empty image)';
                } else if (node.type === 'sfTimeskip') {
                  preview = `${node.timeskipMode === 'set' ? 'Set' : '+'} ${node.hours || 0}h ${node.minutes || 0}m`;
                } else if (node.type === 'sfIntroduce') {
                  preview = node.characterName || node.characterId || '';
                } else if (node.type === 'sfStatus') {
                  preview = `${node.characterName || ''} ${node.statusEmoji || ''}`;
                } else if (node.type === 'sfDecision') {
                  preview = `${node.decisionName || ''}: ${(node.options || []).join(' / ')}`;
                } else if (node.type === 'sfNextChapter') {
                  preview = node.targetChapterName || '';
                }

                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1, p: 1,
                      borderRadius: '6px', bgcolor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)'
                    }}
                  >
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)', minWidth: 18, textAlign: 'right', mt: '1px' }}>
                      {i + 1}
                    </Typography>
                    <Box sx={{
                      px: 0.75, py: 0.25, borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                      color, bgcolor: `${color}18`, whiteSpace: 'nowrap', flexShrink: 0
                    }}>
                      {label}
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {preview}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </AdminModal>

      <StorageBrowserModal
        open={!!storageBrowser}
        onClose={() => setStorageBrowser(null)}
        fileFilter={storageBrowser?.fileFilter || 'image'}
        teamId={flowData?.teamId || ''}
        getToken={getToken}
        title={storageBrowser?.title}
        onSelect={(item) => {
          if (!storageBrowser || !primarySelected) return;
          const { nodeField } = storageBrowser;
          if (nodeField === 'image') {
            updateSelectedData({
              imageName: item.name,
              imageUrl: item.publicUrl || '',
              imagePreview512: item.previewUrls?.s512 || ''
            });
          } else if (nodeField === 'video') {
            updateSelectedData({
              videoName: item.name,
              videoUrl: item.publicUrl || '',
              videoPreview512: item.previewUrls?.s512 || ''
            });
          } else if (nodeField === 'audio') {
            updateSelectedData({ audio: item.name, audioUrl: item.publicUrl || '' });
          }
          setStorageBrowser(null);
        }}
      />

      {/* Check graph notification */}
      <Snackbar
        open={!!checkMsg}
        autoHideDuration={4000}
        onClose={() => setCheckMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {checkMsg ? (
          <Alert severity={checkMsg.severity} variant="filled" onClose={() => setCheckMsg(null)} sx={{ minWidth: 300 }}>
            {checkMsg.text}
          </Alert>
        ) : undefined}
      </Snackbar>

    </Box>
  );
}

export default function StoryflowEditorPage() {
  const { getToken } = useAuthContext();
  return (
    <TeamProvider getToken={getToken} userRole="member">
      <ReactFlowProvider>
        <StoryflowEditorInner />
      </ReactFlowProvider>
    </TeamProvider>
  );
}
